// Rules (docs/app.md → Rules). Default rules for invoice routing: which
// vendor coding rule applies (Codes) and which company a sender's mail
// routes to (Companies). Split out of References (owner item 10a,
// 2026-08-13) — a rule decides WHERE an invoice goes, not just what a master
// record says.
//
// Gated per tab, the way References is: each tab is opened by its own role
// (`rules_vendors`, `rules_guidance`, …) and nothing else — see `App.tsx` and
// `canManageRulesSection` in `backend/src/domain/authority.ts`.
//
// Four sub-tabs: Codes (the vendor coding rules manager, formerly
// References → Defaults, and named Accounts here until owner item 9,
// 2026-08-19 renamed it in parallel with References' own tab), Companies
// (the sender/company routing manager, formerly References → Senders),
// moved verbatim, Vendors (owner item 10b, 2026-08-13) — a rule reading
// "when an invoice arrives from <alias_name>, attribute it to <vendor>",
// backed by `vendor_aliases` (extended with a writer, not a new table —
// `docs/decisions.md`). A match is a DETERMINISTIC OVERRIDE of vendor
// identity, ahead of vendor precedent (owner item 10c; `docs/ai.md` §3.2) —
// stronger than the vendor coding rules on the Codes tab, which are a soft
// preference pass 2 can still weigh against the document — and Guidance
// (docs/ai.md §9, 2026-08-18): the vendor extraction-guidance training loop.
// A repeated correction pattern becomes standing pass-1 guidance authored by
// a nightly worker; this tab lists it, lets an authorised person SUPERSEDE it
// (never an in-place content edit — a supersede is a new version) or RETIRE
// it (`ap_app` holds no DELETE anywhere, so retiring is the only "delete"
// there is), and shows `applied_count`, `last_applied_at`, and the
// before/after correction rate per row. A fourth tab is fine; the point of
// the "same order" note below is that Vendors/Companies/Codes stay in step
// with References' own first three, not that this screen may never grow one
// of its own.
//
// Every list here is one `RuleTable` (`components/RuleTable.tsx`), and the
// dialog, toast and write-error helpers are the shared ones (code review G-10).
import {
  CODING_RULE_SOURCE,
  COMPANY_ALIAS_MIN_KEY_LENGTH,
  VENDOR_ALIAS_SOURCE,
  normalizeAliasName,
  normalizeDescription,
  DOCUMENT_FORM,
  normalizeSenderKey,
  senderKeyFits,
  senderKeyKind,
  senderKeyMisfit,
  senderRuleTaken,
  type DocumentForm,
  type SenderKeyKind,
} from '@ap/shared'
import { GroupingRulesManager } from './rules/GroupingRulesManager'
import { OversightRulesManager } from './rules/OversightRulesManager'
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Body1,
  Button,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  Dropdown,
  Field,
  Option,
  Spinner,
  Tooltip,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components'
import { FieldInput, FieldTextarea } from '../components/FieldControls'
import { AppDialog } from '../components/AppDialog'
import { AddRegular, DismissRegular } from '@fluentui/react-icons'
import { errorMessage, isApiError, writeError } from '../api/errors'
import { ConfirmDialog } from '../components/ConfirmDialog'
import { useToast } from '../components/useToast'
import type { CompanyAlias, VendorAlias, CodingRule, VendorExtractionGuidance } from '../lib/types'
import {
  RULES_SECTIONS,
  RULES_SECTION_LABEL,
  RULES_SECTION_SUBHEADER,
  type RulesSection,
} from '../lib/constants'
import {
  createCompanyAlias,
  createVendorAlias,
  createCodingRule,
  createVendorExtractionGuidance,
  loadCompaniesForManagement,
  loadCompanyAliases,
  loadCompanyEmailDomains,
  loadDepartmentLabels,
  loadDepartmentReach,
  loadAccountLabels,
  loadIntakeSenders,
  loadSenderFormatDefaults,
  loadInAppUploadBlocks,
  loadVendorAliases,
  loadCodingRules,
  loadVendorExtractionGuidance,
  saveSenderRule,
  searchDepartments,
  searchAccounts,
  searchVendors,
  supersedeVendorExtractionGuidance,
  updateCompanyAlias,
  updateVendorAlias,
  codingRuleChanges,
  nothingChanged,
  updateCodingRule,
  updateVendorExtractionGuidanceActive,
} from '../lib/data'
import {
  ALLOWED_WORDS,
  FORMAT_WORDS,
  SENDER_KIND_WORDS,
  companyWords,
  activeEntryFor,
  clauseRequest,
  entryIsActive,
  mergeSenderEntries,
  routedCompanyName,
  ruleFormat,
  ruleInAppAllowed,
  ruleIntake,
  ruleRouting,
  statusRequest,
  storedKeyNotice,
  uploaderRuleSentence,
  type SenderEntry,
} from '../lib/senders'
import {
  closeSentence,
  emptiedScopeWarning,
  guidanceReplacesNotice,
  NOTHING_CHANGED,
  savedToast,
  statusToast,
  vendorOptionSubtext,
} from '../lib/format'
import { StatusToggle } from '../components/StatusToggle'
import { PageTabs } from '../components/PageTabs'
import { KeptView } from '../components/KeptView'
import { ScreenIntro } from '../components/ScreenIntro'
import { ErrorBanner } from '../components/States'
import { useDialogError } from '../lib/hooks'
import { DEFAULT_STATUS_FILTER, matchesStatus, type StatusFilter } from '../lib/statusFilter'
import { EntityPicker, type EntityOption } from '../components/EntityPicker'
import { ListFilters } from '../components/ListFilters'
import { DEFAULT_RULE_SOURCE_FILTER, RULE_SOURCE_OPTIONS, matchesRuleSource } from '../lib/ruleSource'
import { FrozenWhileClosed } from '../components/FrozenWhileClosed'
import { ScreenActions } from '../components/ScreenActions'
import { RuleTable } from '../components/RuleTable'
import { SegmentedControl } from '../components/SegmentedControl'
import { StatusDot } from '../components/StatusDot'
import { popupPositionProps, usePopupListboxClass } from '../components/popupPositioning'
import { useDialogFormStyles } from '../components/dialogForm'

const useStyles = makeStyles({
  pickerRow: { display: 'flex', gap: '8px', alignItems: 'flex-end' },
  // Inline, non-blocking dialog warning (mirrors States.tsx warningBanner and
  // TrainingScreen.tsx's identical class — see this file's header comment on
  // why the two are separate copies rather than a shared import).
  dialogWarn: {
    display: 'block',
    color: tokens.colorPaletteYellowForeground2,
    backgroundColor: tokens.colorPaletteYellowBackground1,
    border: `1px solid ${tokens.colorPaletteYellowBorder1}`,
    borderRadius: tokens.borderRadiusMedium,
    padding: '8px 12px',
  },
  guidanceStatusCell: { display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-start' },
  guidanceConverged: {
    color: tokens.colorPaletteRedForeground1,
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase200,
  },
})

/** What every Rules list keys its rows and bulk strip by. Module-level, so the
    identity is stable across renders. */
const rowId = (row: { id: string }) => row.id
const rowIsActive = (row: { is_active: boolean }) => row.is_active
const senderKey = (e: SenderEntry) => e.key
const writeCompanyAliasActive = (a: CompanyAlias, active: boolean) =>
  updateCompanyAlias(a.id, { is_active: active, row_version: a.row_version })
const writeCodingRuleActive = (r: CodingRule, active: boolean) =>
  updateCodingRule(r.id, { is_active: active, row_version: r.row_version })
const writeVendorAliasActive = (a: VendorAlias, active: boolean) =>
  updateVendorAlias(a.id, { is_active: active, row_version: a.row_version })

// Vendors, Companies, Codes — deliberately the same order as the References
// screen's own first three tabs (`TrainingScreen.tsx`), so the two master-data
// screens read the same way round. Keep them in step: if one is reordered, the
// parallel is the whole point of the order and the other should follow.
// Guidance is a fourth tab with no References counterpart — it trails the
// other three rather than disturbing their shared order.
//
// 'accounts' renamed to 'codes' (owner item 9, 2026-08-19), in parallel with
// the References screen's own rename — this tab's CONTENT is unchanged
// (`CodingRulesManager`, the vendor coding rules manager, independent of
// the word "Account" already), only its label and tab value follow suit.
type RuleTab = 'vendors' | 'companies' | 'codes' | 'grouping' | 'guidance' | 'oversight'

// Section value -> this screen's tab value. Here the two are the same for every
// section; References' own map differs (its three line-level sections open the
// Additional Detail and Associated Entities tabs, D-193). The map stays so a new section is a compile error here.
const TAB_FOR_SECTION: Readonly<Record<RulesSection, RuleTab>> = {
  vendors: 'vendors',
  companies: 'companies',
  codes: 'codes',
  grouping: 'grouping',
  guidance: 'guidance',
  oversight: 'oversight',
}

/** The inverse, for reading the selected tab's subheader out of a map keyed by
    SECTION — the key every other `RULES_SECTION_*` map uses. Spelt out rather
    than derived from `TAB_FOR_SECTION` so a fifth tab is a compile error here
    too, and so a future difference between the two vocabularies is written
    down in both directions. */
const SECTION_FOR_TAB: Readonly<Record<RuleTab, RulesSection>> = {
  vendors: 'vendors',
  companies: 'companies',
  codes: 'codes',
  grouping: 'grouping',
  guidance: 'guidance',
  oversight: 'oversight',
}

/** The header tabs, left to right. "Codes" again since 2026-09-01 (owner), in
    step with References' own tab; "Guidance" is the AI guidance tab and has
    nothing to do with GL codes, so item 27's rename to "Accounts &
    Departments" was not applied to it. Oversight Approval (owner, 2026-09-15)
    trails Guidance rather than joining the first three, whose order
    deliberately mirrors the References screen's own. */
const RULE_TAB_ORDER: readonly RuleTab[] = ['vendors', 'companies', 'codes', 'grouping', 'guidance', 'oversight']

/** The two masters behind Rules → Companies. Uploaders routes an incoming
    document by who sent it — one address or a whole domain; Aliases routes it
    by the name printed on the invoice. Left to right, and the order the arrow
    keys walk. The label is Uploaders since 2026-10-19 (owner, D-379: "I would
    also change that view to 'Uploaders' rather than 'Domains'"); the token
    stays `domains`, a view's internal name that nothing outside this file
    reads. */
type CompanyView = 'domains' | 'aliases'

const COMPANY_VIEWS: readonly { value: CompanyView; label: string }[] = [
  { value: 'domains', label: 'Uploaders' },
  { value: 'aliases', label: 'Aliases' },
]

export function RulesScreen({ sections = RULES_SECTIONS }: { sections?: readonly RulesSection[] }) {
  // Owner item 29, 2026-08-28: a user sees only the tabs they hold a role for.
  // `sections` arrives from `visibleRuleTabs(session)` in App.tsx, which
  // carries the server's `capabilities.rules_sections`, with no fallback since
  // the coarse role went (D-293). A HINT, NEVER THE BOUNDARY — every write route re-asks
  // `canManageRulesSection`.
  const shown = useMemo(() => sections.map((section) => TAB_FOR_SECTION[section]), [sections])
  const [tab, setTab] = useState<RuleTab>(shown[0] ?? 'vendors')
  // Which of the Companies tab's two masters is showing. Lifted to the screen
  // because the switcher renders in the tab row, above both managers.
  const [companiesView, setCompaniesView] = useState<CompanyView>('domains')
  // The grant can change under a live tab; fall back to the first still offered.
  useEffect(() => {
    if (shown.length > 0 && !shown.includes(tab)) setTab(shown[0])
  }, [shown, tab])
  return (
    <div>
      {/* THE TABS RIDE THE HEADER, and the Companies view switcher rides the
          same row beside them (owner, 2026-09-14: "I am not in love with a
          slider under a tab under a tab"; 2026-09-28 moved the row itself into
          the header, `components/PageTabs.tsx`). One view at a time: the two
          masters behind Companies used to be two stacked tables, each with a
          toolbar of its own. */}
      <PageTabs
        tabs={RULE_TAB_ORDER.filter((t) => shown.includes(t)).map((t) => ({
          value: t,
          label: RULES_SECTION_LABEL[SECTION_FOR_TAB[t]],
        }))}
        selectedValue={tab}
        onSelect={setTab}
        ariaLabel="Rules tables"
        trailing={
          tab === 'companies' && shown.includes('companies') ? (
            <SegmentedControl
              ariaLabel="Which company rules to show"
              value={companiesView}
              options={COMPANY_VIEWS}
              onChange={setCompaniesView}
            />
          ) : undefined
        }
      />
      {/* PER TAB (owner, 2026-09-11): the intro swaps with the selection,
          because the five tabs do genuinely different jobs — only Companies is
          about routing. Each sentence lives in `RULES_SECTION_SUBHEADER` and is
          rendered here ONLY, never also inside the manager below. */}
      <ScreenIntro>
        {RULES_SECTION_SUBHEADER[SECTION_FOR_TAB[tab]]}
      </ScreenIntro>
      {tab === 'vendors' && shown.includes('vendors') && <VendorAliasesManager />}
      {/* OWNER ITEM 18, 2026-08-31. The owner was unsure whether company
          aliases belong in References → Companies or Rules → Companies. Rules,
          because this tab already IS "how a company is decided for an incoming
          document" — the domain rung lives here, and the alias rung is the rung
          immediately below it. References → Companies is where a company's own
          attributes are maintained; an alias is not an attribute of the
          company, it is a routing rule about a name. Same gate either way
          (`rules_companies`), so this is a placement choice, not an authority
          one. Stacked until 2026-09-14; two views of one tab since.
          BOTH VIEWS MOUNT WITH THE TAB and the one not chosen is hidden
          (`components/KeptView.tsx`), so a switch is one
          paint, as on References → Codes, not a reload behind a spinner
          (owner, 2026-10-07). */}
      {tab === 'companies' && shown.includes('companies') && (
        <>
          <KeptView active={companiesView === 'domains'}>
            <SendersManager />
          </KeptView>
          <KeptView active={companiesView === 'aliases'}>
            <CompanyAliasesManager />
          </KeptView>
        </>
      )}
      {tab === 'codes' && shown.includes('codes') && <CodingRulesManager />}
      {/* Rules -> Grouping (owner, 2026-10-22 — D-402), beside Codes: its own
          role, `rules_grouping`. */}
      {tab === 'grouping' && shown.includes('grouping') && <GroupingRulesManager />}
      {tab === 'guidance' && shown.includes('guidance') && <GuidanceManager />}
      {/* One list since D-336 (owner, 2026-10-09): each rule says which
          approvals it requires, so the Controller / Oversight switcher of
          D-304 is gone. */}
      {tab === 'oversight' && shown.includes('oversight') && <OversightRulesManager />}
      {/* Upload Notifications (owner, 2026-10-07 — D-306), after Routing. */}
    </div>
  )
}

// ---- Company aliases (Rules → Companies, owner item 18, 2026-08-31) -------
//
// "Some companies have aliases. For example, SeniorLIFE Johnstown (251) also
// goes by Pennsylvania PACE, Inc., Pennsylvania PACE, PA PACE, and SLJ. These
// aliases should be taken into account when the matching agent is attempting
// to discern the appropriate company for routing."
//
// Modelled on the Vendors tab's alias manager one-for-one: the rule reads back
// as a sentence, the add button says "Add Rule" like the rest of this screen,
// and there is no delete — retiring is a Status change.

/**
 * THE ALIAS FLOOR, `COMPANY_ALIAS_MIN_KEY_LENGTH` from `@ap/shared`: counted
 * on the key the matching ladder compares, so `P.A.` is two, not four. The
 * server refuses a shorter alias; this only keeps Save disabled so nobody types
 * one and waits for a round trip to be told.
 */
const COMPANY_ALIAS_MIN_KEY = COMPANY_ALIAS_MIN_KEY_LENGTH
const companyAliasKey = (value: string): string => value.toLowerCase().replace(/[^a-z0-9]+/g, '')


function CompanyAliasesManager() {
  const toast = useToast()
  const [all, setAll] = useState<CompanyAlias[] | null>(null)
  const [activeCompanies, setActiveCompanies] = useState<{ id: string; name: string }[]>([])
  const [status, setStatus] = useState<StatusFilter>(DEFAULT_STATUS_FILTER)
  const [error, setError] = useState<string | null>(null)
  const [edit, setEdit] = useState<CompanyAlias | 'new' | null>(null)
  const [term, setTerm] = useState('')

  const load = useCallback(async () => {
    setError(null)
    try {
      const [aliases, companies] = await Promise.all([
        loadCompanyAliases(),
        loadCompaniesForManagement(),
      ])
      setAll(aliases)
      setActiveCompanies(
        companies
          .filter((c) => c.is_active !== false)
          .map((c) => ({ id: c.company_id, name: c.company_name })),
      )
    } catch (e) {
      setError(errorMessage(e))
      setAll([])
    }
  }, [])
  useEffect(() => {
    void load()
  }, [load])

  // `company_aliases.company_name` is a denormalized cache written at create
  // time, exactly like `vendor_aliases.vendor_name` — always populated, so
  // there is no id-to-name lookup to run here.
  const companyName = useCallback((a: CompanyAlias) => a.company_name || a.company_id, [])

  const sentence = useCallback(
    (a: CompanyAlias): string =>
      closeSentence(`When an invoice is billed to "${a.alias_name}", attribute the invoice to ${companyName(a)}`),
    [companyName],
  )

  const visible = useMemo(() => {
    const q = term.trim().toLowerCase()
    const haystack = (a: CompanyAlias) =>
      [a.alias_name, a.company_id, companyName(a)].join(' ').toLowerCase()
    return (all ?? [])
      .filter((a) => matchesStatus(status, a.is_active))
      .filter((a) => !q || haystack(a).includes(q))
  }, [all, status, term, companyName])

  if (all === null) return <Spinner label="Loading company alias rules…" />

  return (
    <div>
      {error && <ErrorBanner message={error} />}
      {/* The page's own tools live in the header's top-right cluster since
          2026-09-14 (owner) — see `components/ScreenActions.tsx`. */}
      <ScreenActions>
        <Tooltip content="Add Rule" relationship="label">
          <Button appearance="secondary" icon={<AddRegular />} onClick={() => setEdit('new')} />
        </Tooltip>
        <ListFilters
          status={status}
          onStatusChange={setStatus}
          term={term}
          onTermChange={setTerm}
          searchPlaceholder="Search text or company…"
          ariaLabel="Company Alias Rules"
        />
      </ScreenActions>
      <RuleTable
        rows={visible}
        emptyText={(all ?? []).length === 0 ? 'No rules to show.' : 'No rules match.'}
        ariaLabel="Company alias rules"
        selectAllLabel="company alias rules"
        keyOf={rowId}
        isActive={rowIsActive}
        sentence={sentence}
        write={writeCompanyAliasActive}
        noun="rule"
        onEdit={setEdit}
        onMarked={(m: string) => {
          toast(m)
          void load()
        }}
        onError={setError}
      />
      <CompanyAliasDialog
        row={edit}
        siblings={all ?? []}
        activeCompanies={activeCompanies}
        companyName={companyName}
        onClose={() => setEdit(null)}
        onSaved={(message) => {
          setEdit(null)
          toast(message)
          void load()
        }}
      />
    </div>
  )
}

function CompanyAliasDialog({
  row,
  siblings,
  activeCompanies,
  companyName,
  onClose,
  onSaved,
}: {
  row: CompanyAlias | 'new' | null
  /** Every rule the manager has loaded, so the form can say an alias is taken
      while it is being typed — `VendorAliasDialog`'s `siblings`, for the same
      reason. The server refuses the same thing (`assertAliasKeyFree`). */
  siblings: readonly CompanyAlias[]
  activeCompanies: readonly { id: string; name: string }[]
  companyName: (a: CompanyAlias) => string
  onClose: () => void
  /** The toast, from `savedToast` — the dialog knows whether Status moved. */
  onSaved: (message: string) => void
}) {
  const s = useStyles()
  const df = useDialogFormStyles()
  // THIS DIALOG'S OWN REFUSAL (QA wave A, 2026-09-22, A-01). It used to go
  // to the manager's screen banner, which renders behind the modal.
  const [saveError, setSaveError] = useDialogError(row)
  const isNew = row === 'new'
  const existing = row && row !== 'new' ? row : null
  /** See `VendorDefaultDialog` above for why the surface is resolved here. */
  const [dialogSurface, setDialogSurface] = useState<HTMLElement | null>(null)
  const listboxClass = usePopupListboxClass()
  const [aliasText, setAliasText] = useState('')
  const [companyId, setCompanyId] = useState<string>('')
  const [isActive, setIsActive] = useState(true)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!row) return
    if (row === 'new') {
      setAliasText('')
      setCompanyId('')
      setIsActive(true)
      return
    }
    setAliasText(row.alias_name)
    setCompanyId(row.company_id)
    setIsActive(row.is_active)
  }, [row])

  const normalizedAlias = normalizeAliasName(aliasText)
  const aliasChanged = aliasText.trim() !== '' && normalizedAlias !== aliasText.trim()
  const tooShort =
    normalizedAlias !== '' && companyAliasKey(normalizedAlias).length < COMPANY_ALIAS_MIN_KEY
  // ONE ACTIVE RULE PER ALIAS (QA wave B, 2026-09-25, B-08; D-172) — the
  // Vendors tab's own guard, compared on the COMPANY KEY because that is what
  // the ladder compares: `PA PACE` and `PA-PACE` are one alias. An Inactive
  // rule is not in the way, and a rule left Inactive never collides.
  const takenBy =
    normalizedAlias === '' || tooShort || !isActive
      ? null
      : (siblings.find(
          (a) =>
            a.is_active &&
            a.id !== existing?.id &&
            companyAliasKey(a.alias_name) === companyAliasKey(normalizedAlias),
        ) ?? null)
  const ready = normalizedAlias !== '' && !tooShort && companyId !== '' && takenBy === null
  // A retired company still has to render its own name on a rule that already
  // points at it, so the selected label falls back to the stored one.
  const selectedName =
    activeCompanies.find((c) => c.id === companyId)?.name ??
    (existing && existing.company_id === companyId ? companyName(existing) : '')

  async function save() {
    if (!ready) return
    setSaving(true)
    try {
      if (isNew) {
        await createCompanyAlias({ alias_name: normalizedAlias, company_id: companyId })
      } else if (existing) {
        const next = { alias_name: normalizedAlias, company_id: companyId, is_active: isActive }
        // A Save that changed nothing writes nothing (D-170).
        if (nothingChanged(existing, next)) {
          onSaved(NOTHING_CHANGED)
          return
        }
        await updateCompanyAlias(existing.id, { ...next, row_version: existing.row_version })
      }
      onSaved(savedToast('rule', { wasNew: isNew, activeBefore: existing?.is_active, activeAfter: isActive }))
    } catch (e) {
      setSaveError(writeError(e, 'Could not save rule'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <AppDialog open={!!row} onOpenChange={(_, d) => !d.open && onClose()}>
      <DialogSurface ref={setDialogSurface}>
        <FrozenWhileClosed open={!!row}><DialogBody>
          <DialogTitle>{isNew ? 'Add Rule' : 'Edit Rule'}</DialogTitle>
          <DialogContent className={mergeClasses(df.stack, df.beforeActions)}>
            <ErrorBanner message={saveError} />
            {/* The clauses of the rule's own sentence, in order (owner,
                2026-09-14 — see `VendorAliasDialog` for the whole rule):

                  When an invoice is billed to "Pennsylvania PACE, Inc.",
                  attribute the invoice to SeniorLIFE Johnstown.

                "billed to" rather than "arrives from", because that is the
                name this rule actually matches on — the bill-to name as
                printed, which is what the hint that stood here said. The rest
                of that hint (case, spacing and punctuation are ignored) is
                what the two lines below say, when they have something to
                say. */}
            <Field label="When an invoice is billed to">
              <FieldInput
                value={aliasText}
                onChange={(_, d) => setAliasText(d.value)}
                placeholder="e.g. Pennsylvania PACE, Inc."
              />
            </Field>
            {aliasChanged && !tooShort && takenBy === null && (
              <Body1 className={s.dialogWarn}>
                Stored as &ldquo;{normalizedAlias}&rdquo; — leading, trailing and repeated spaces are
                collapsed.
              </Body1>
            )}
            {takenBy !== null && (
              <Body1 className={s.dialogWarn}>
                Already in use: &ldquo;{takenBy.alias_name}&rdquo; attributes invoices to{' '}
                {companyName(takenBy)}. Make that rule Inactive, or edit it, rather than adding a second
                one — two rules naming one printed name would leave both doing nothing.
              </Body1>
            )}
            {tooShort && (
              <Body1 className={s.dialogWarn}>
                Too short: a company name rule needs at least {COMPANY_ALIAS_MIN_KEY} letters or
                digits, so it cannot match half the invoices that arrive.
              </Body1>
            )}
            <Field label="attribute the invoice to">
              <Dropdown
                value={selectedName}
                selectedOptions={companyId === '' ? [] : [companyId]}
                placeholder="e.g. SeniorLIFE Johnstown"
                onOptionSelect={(_, d) => setCompanyId(d.optionValue ?? '')}
                listbox={{ className: listboxClass }}
                {...popupPositionProps(dialogSurface)}
              >
                {activeCompanies.map((c) => (
                  <Option key={c.id} value={c.id} text={c.name}>
                    {c.name}
                  </Option>
                ))}
              </Dropdown>
            </Field>
          </DialogContent>
          {!isNew && (
            <DialogActions position="start">
              <StatusToggle value={isActive} onChange={setIsActive} />
            </DialogActions>
          )}
          <DialogActions>
            <Button appearance="secondary" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button appearance="primary" disabled={!ready || saving} onClick={() => void save()}>
              Save
            </Button>
          </DialogActions>
        </DialogBody></FrozenWhileClosed>
      </DialogSurface>
    </AppDialog>
  )
}

// ---- Codes (vendor coding rules, formerly References → Defaults; the tab was
// Accounts until owner item 9 made it Codes on 2026-08-19, Accounts &
// Departments from owner item 26 on 2026-08-28, and Codes again since
// 2026-09-01) ------------------------------------------------------------------
//
// A rule names an account and, optionally, a department (G13), and may be
// narrowed to lines whose description contains a phrase. Each one reads back as a full sentence,
// with no second line and no source annotation. Where a rule came from is the
// Source filter's job since D-367 (Rules or Memory), not a line on every row:
//
//   When a line item from GORDON FOOD SERVICE contains "produce",
//   default that line item's Account to Food & Staples and Department to
//   Dietary.
//
// WORDING, owner item 26 (2026-08-28), three corrections in one sentence:
//   * "has any line" / "has a line mentioning" was unclear, and it hid that
//     the scope is a CONTAINS test, not an equals test — so the lead clause
//     now says `contains "…"` outright.
//   * The sentence still used the halves' retired names. The rest of the app
//     had moved to Account and Department; this tab was missed. Only what a
//     person reads changed then; the wire names (`account_id`,
//     `department_id`) followed in the 2026-09 terminology rename.
//   * "that line item's" makes the per-line grain explicit. This fires per
//     LINE ITEM during the pass-2 matching run
//     (backend/src/domain/matchingLadder.ts, backend/src/lambda/match.ts) —
//     not "when an invoice is uploaded", which is a different, false claim
//     about the same rule.
//
// A blank scope reads "For every line item from …" instead, and a rule
// naming only one half drops the other clause.



/** A coding rule a posted invoice wrote, not a person (D-367). */
const isMemoryRule = (r: CodingRule): boolean => r.source === CODING_RULE_SOURCE.Learned

/** The Source multi-select every Rules tab with Memory carries (D-367). */
function sourceSelect(values: string[], onChange: (values: string[]) => void) {
  return {
    multiple: true as const,
    label: 'Source',
    values,
    onChange,
    options: RULE_SOURCE_OPTIONS,
    defaultValues: DEFAULT_RULE_SOURCE_FILTER,
  }
}

function CodingRulesManager() {
  const toast = useToast()
  const [all, setAll] = useState<CodingRule[] | null>(null)
  const [status, setStatus] = useState<StatusFilter>(DEFAULT_STATUS_FILTER)
  const [source, setSource] = useState<string[]>(DEFAULT_RULE_SOURCE_FILTER)
  const [error, setError] = useState<string | null>(null)
  const [edit, setEdit] = useState<CodingRule | 'new' | null>(null)
  const [term, setTerm] = useState('')
  const [accountNames, setAccountNames] = useState<ReadonlyMap<string, string>>(new Map())
  const [departmentNames, setDepartmentNames] = useState<ReadonlyMap<string, string>>(new Map())

  const load = useCallback(async () => {
    setError(null)
    try {
      // The two halves come from the LABEL loaders, not the pickers: they are
      // memoized, they ask for the whole list, and they carry no `active`
      // filter. A rule may name a half retired under it — that rule still has
      // to read back as a sentence rather than as digits.
      const [rules, accounts, departments] = await Promise.all([
        loadCodingRules(),
        loadAccountLabels().catch(() => new Map<string, string>()),
        loadDepartmentLabels().catch(() => new Map<string, string>()),
      ])
      setAll(rules)
      setAccountNames(accounts)
      setDepartmentNames(departments)
    } catch (e) {
      setError(errorMessage(e))
      setAll([])
    }
  }, [])
  useEffect(() => {
    void load()
  }, [load])

  // The vendor name rides the row itself now — `GET /coding-rules`
  // resolves it server-side with a read-time join onto `vendors`
  // (`docs/api.md` §15.13–15.15) — so there is no id-to-name lookup to run
  // here any more. `vendor_id` is the last-resort fallback, for the
  // unreachable case the join itself already treats as its own fallback.
  // `Any vendor` (G1) is a real answer, not a missing one, so it reads as such
  // everywhere the vendor is shown — in the sentence, in the search haystack and
  // in the editor. A blank would be indistinguishable from a row whose join
  // failed, which is the case `vendor_id` is the fallback for.
  const vendorName = useCallback(
    (r: CodingRule) => (r.vendor_id === null ? 'any vendor' : r.vendor_name || r.vendor_id),
    [],
  )
  const accountName = useCallback(
    (id?: string | null) => (id ? accountNames.get(id) ?? id : ''),
    [accountNames],
  )
  const departmentName = useCallback(
    (id?: string | null) => (id ? departmentNames.get(id) ?? id : ''),
    [departmentNames],
  )

  /** The rule as a full, true sentence — the whole point of the row, and the
      exact phrasing the editor mirrors (owner item 26). It fires per LINE ITEM
      during pass-2 matching, so the sentence is about a line item, never "when
      an invoice is uploaded". A rule names either half of a code, or both —
      never a whole code — and the CHECK constraint
      `vendor_coding_rule_says_something` bars neither being set, but the final
      fallback below covers that case anyway rather than emitting a dangling
      "default that line item's". */
  const sentence = useCallback(
    (r: CodingRule): string => {
      const who = vendorName(r)
      const scope = r.description_scope?.trim()
      // "line item", not "invoice line item" (owner, 2026-09-14). The dialog's
      // own first clause says "When a line item from", and the owner's ask was
      // that a rule be entered "in plain language, like they later present it"
      // — so the two have to be the same words, not nearly the same.
      // MEMORY (D-367) matches the WHOLE printed description, not a phrase in
      // it, so it says "is", not "contains".
      const lead = isMemoryRule(r)
        ? `When a line item from ${who} is "${scope ?? ''}"`
        : scope
          ? `When a line item from ${who} contains "${scope}"`
          : `For every line item from ${who}`
      const account = r.account_id ? accountName(r.account_id) : null
      const department = r.department_id ? departmentName(r.department_id) : null
      if (account && department) {
        return closeSentence(`${lead}, default that line item's Account to ${account} and Department to ${department}`)
      }
      if (account) return closeSentence(`${lead}, default that line item's Account to ${account}`)
      if (department) return closeSentence(`${lead}, default that line item's Department to ${department}`)
      return `${lead}.`
    },
    [vendorName, accountName, departmentName],
  )

  const visible = useMemo(() => {
    const q = term.trim().toLowerCase()
    // Search the STRUCTURED fields, not the sentence: the sentence's own
    // template words ("Account", "Department", "invoice", "line item",
    // "contains", "default") would otherwise match every row.
    const haystack = (d: CodingRule) =>
      [
        d.vendor_id,
        vendorName(d),
        d.description_scope ?? '',
        d.account_id ?? '',
        d.department_id ?? '',
        accountName(d.account_id),
        departmentName(d.department_id),
      ]
        .join(' ')
        .toLowerCase()
    return (all ?? [])
      .filter((d) => matchesStatus(status, d.is_active))
      .filter((d) => matchesRuleSource(source, isMemoryRule(d)))
      .filter((d) => !q || haystack(d).includes(q))
  }, [all, status, source, term, vendorName, accountName, departmentName])

  if (all === null) return <Spinner label="Loading rules…" />

  return (
    <div>
      {error && <ErrorBanner message={error} />}
      {/* The sentence that stood here (owner item 26, 2026-08-28) is the tab's
          subheader since 2026-09-11 — moved, not deleted. */}
      {/* The page's own tools live in the header's top-right cluster since
          2026-09-14 (owner) — see `components/ScreenActions.tsx`. */}
      <ScreenActions>
        <Tooltip content="Add Rule" relationship="label">
          <Button appearance="secondary" icon={<AddRegular />} onClick={() => setEdit('new')} />
        </Tooltip>
        <ListFilters
          status={status}
          onStatusChange={setStatus}
          term={term}
          onTermChange={setTerm}
          searchPlaceholder="Search vendor, Account, or Department…"
          ariaLabel="Coding Rules"
          selects={[sourceSelect(source, setSource)]}
        />
      </ScreenActions>
      <RuleTable
        rows={visible}
        emptyText={(all ?? []).length === 0 ? 'No rules to show.' : 'No rules match.'}
        ariaLabel="Vendor coding rules"
        selectAllLabel="vendor coding rules"
        keyOf={rowId}
        isActive={rowIsActive}
        sentence={sentence}
        write={writeCodingRuleActive}
        noun="rule"
        onEdit={setEdit}
        onMarked={(m: string) => {
          toast(m)
          void load()
        }}
        onError={setError}
      />
      <VendorDefaultDialog
        row={edit}
        vendorName={vendorName}
        accountName={accountName}
        departmentName={departmentName}
        onClose={() => setEdit(null)}
        onSaved={(message) => {
          setEdit(null)
          toast(message)
          void load()
        }}
      />
    </div>
  )
}

/** THE PINNED FIRST OPTION in the Codes dialog's vendor picker (owner,
    2026-09-11). A sentinel id, not a real `vendor_id`: it never reaches the
    server — `doSave` reads `anyVendor` and writes `vendor_id: null`, which is
    what "any vendor" has always been stored as. The value is `__any_vendor__`
    rather than anything shorter because a vendor id is the controller's own
    code and could plausibly be almost any short string. */
const ANY_VENDOR_ID = '__any_vendor__'
const ANY_VENDOR_LABEL = 'Any vendor'
const ANY_VENDOR_OPTION: EntityOption = { id: ANY_VENDOR_ID, primary: ANY_VENDOR_LABEL }
/** What `EntityPicker` shows in the box once it is chosen. */
const ANY_VENDOR_OPTION_VALUE = { id: ANY_VENDOR_ID, name: ANY_VENDOR_LABEL }

/** Whether the pinned option belongs in the results for this search term.
    Always on the first open (the empty term), and thereafter only while the
    reader is typing something the label itself contains — offered against an
    unrelated term it would read as a search result, which is the one thing it
    is not. */
function anyVendorMatches(term: string): boolean {
  const t = term.trim().toLowerCase()
  return t === '' || ANY_VENDOR_LABEL.toLowerCase().includes(t)
}

function VendorDefaultDialog({
  row,
  vendorName,
  accountName,
  departmentName,
  onClose,
  onSaved,
}: {
  row: CodingRule | 'new' | null
  /** Resolvers from `CodingRulesManager`'s label loaders — used only to seed
      an EDIT with names instead of raw ids; a fresh pick already carries its
      own label via `EntityPicker`'s `onSelect`. */
  vendorName: (r: CodingRule) => string
  accountName: (id?: string | null) => string
  departmentName: (id?: string | null) => string
  onClose: () => void
  /** The toast, from `savedToast` — the dialog knows whether Status moved. */
  onSaved: (message: string) => void
}) {
  const s = useStyles()
  const df = useDialogFormStyles()
  // THIS DIALOG'S OWN REFUSAL (QA wave A, 2026-09-22, A-01). It used to go
  // to the manager's screen banner, which renders behind the modal.
  const [saveError, setSaveError] = useDialogError(row)
  const isNew = row === 'new'
  const existing = row && row !== 'new' ? row : null
  /** The dialog's own surface element, resolved by callback ref and handed to
      `popupPositionProps` so this dialog's `Dropdown` listboxes mount inside
      the focus trap (app/README.md's Fluent gotcha list). */
  const [dialogSurface, setDialogSurface] = useState<HTMLElement | null>(null)
  const listboxClass = usePopupListboxClass()
  const [vendor, setVendor] = useState<{ id?: string; name?: string }>({})
  /** G1: an explicit CHOICE, never a blank field — "any vendor" is a decision.
      Still a decision as of 2026-09-11; it is now made by picking the picker's
      own first option rather than by a checkbox beside it (see the Vendor field
      below). */
  const [anyVendor, setAnyVendor] = useState(false)
  const [account, setAccount] = useState<{ id?: string; name?: string }>({})
  const [department, setDepartment] = useState<{ id?: string; name?: string }>({})
  const [scope, setScope] = useState('')
  /** "Any text" or "Specific text" (owner, 2026-09-11). The stored column is
      unchanged — `description_scope` is still a string or NULL — but an empty
      box and a box nobody has reached yet look identical, and that box is the
      difference between a rule that codes one kind of line item and one that
      codes every line item the vendor ever sends. Asking the question outright
      makes the blank case an answer.

      NULL IS "NOT ANSWERED YET", and it is where a new rule starts (owner,
      2026-09-11: "do not default to 'Any text' at the start; let the user pick
      or wait until 'Any vendor' is at least chosen"). Defaulting to "Any text"
      pre-answered the question with the more dangerous of the two answers —
      a rule that codes every line item the vendor sends — and made a dialog
      nobody had touched look complete. Save stays disabled until it is
      answered. Picking "Any vendor" answers it, as "Specific text", because
      that is the only answer the pair permits. */
  const [scopeMode, setScopeMode] = useState<'any' | 'specific' | null>(null)
  const [isActive, setIsActive] = useState(true)
  const [saving, setSaving] = useState(false)
  /** How constrained a Department-only rule is: it applies only where it composes. */
  const [departmentReach, setDepartmentReach] = useState<{ fits: number; total: number } | null>(null)

  useEffect(() => {
    if (!row) return
    if (row === 'new') {
      setVendor({})
      setAnyVendor(false)
      setAccount({})
      setDepartment({})
      setScope('')
      setScopeMode(null)
      setIsActive(true)
      return
    }
    setIsActive(row.is_active)
    // Seed with NAMES, not ids — an edit used to open showing "67-502"
    // rather than the account's actual name.
    setVendor(row.vendor_id === null ? {} : { id: row.vendor_id, name: vendorName(row) })
    setAnyVendor(row.vendor_id === null)
    setAccount({
      id: row.account_id ?? undefined,
      name: row.account_id ? accountName(row.account_id) : undefined,
    })
    setDepartment({
      id: row.department_id ?? undefined,
      name: row.department_id ? departmentName(row.department_id) : undefined,
    })
    setScope(row.description_scope ?? '')
    setScopeMode(row.description_scope ? 'specific' : 'any')
    // `row` is the only thing this effect seeds from — the label resolvers are
    // memoized callbacks from the manager and stable across a render, and
    // listing them here would not change when they fire, only add churn.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row])

  // The reach warning is only meaningful on a department-ONLY rule: with an
  // account set the pair is fixed and the server refuses a non-composing
  // one. Computed over the WHOLE chart (`loadDepartmentReach`), not one page —
  // see that function's docstring for the defect a page-bounded count caused.
  useEffect(() => {
    if (!department.id || account.id) {
      setDepartmentReach(null)
      return
    }
    let cancelled = false
    loadDepartmentReach(department.id)
      .then((reach) => {
        if (!cancelled) setDepartmentReach(reach)
      })
      .catch(() => !cancelled && setDepartmentReach(null))
    return () => {
      cancelled = true
    }
  }, [department.id, account.id])

  const wantsScope = scopeMode === 'specific'
  // What the rule will actually store. "Any text" means NULL regardless of what
  // is still sitting in the box — the box is not rendered in that mode, and a
  // value the reader cannot see must never reach the server.
  const normalizedScope = wantsScope ? normalizeDescription(scope) : ''
  const scopeChanged = wantsScope && scope.trim() !== '' && normalizedScope !== scope.trim().toLowerCase()
  const scopeEmptied = wantsScope && scope.trim() !== '' && normalizedScope === ''
  // WHAT MAKES A RULE SAVEABLE (G13, G2). Mirrors the server rather than
  // replacing it — the route and the CHECK constraints are the gate, and a
  // pre-flight check the server then re-checks is a race. Both halves set is
  // validated by the SERVER alone, which refuses a pair that is not a real
  // code; this screen does not keep the chart.
  //
  //   * an ACCOUNT is required; the Department is optional;
  //   * a vendor, or an explicit "any vendor" — never neither;
  //   * with "any vendor", a description to match on is required, since the
  //     rule would otherwise code every line item the system has never seen;
  //   * and having ASKED for specific text (2026-09-11), that text is required
  //     — "Specific text" with an empty box is a half-made choice, not a rule
  //     matching everything — and the question itself must be ANSWERED, since
  //     it no longer starts on a default.
  const vendorAnswered = anyVendor || !!vendor.id
  const ready =
    vendorAnswered &&
    scopeMode !== null &&
    !!account.id &&
    (!wantsScope || normalizedScope !== '') &&
    (!anyVendor || normalizedScope !== '')

  async function save() {
    if (!ready) return
    setSaving(true)
    try {
      const body = {
        vendor_id: anyVendor ? null : (vendor.id ?? null),
        description_scope: normalizedScope === '' ? null : normalizedScope,
        account_id: account.id ?? null,
        department_id: department.id ?? null,
      }
      if (isNew) {
        await createCodingRule(body)
      } else if (existing) {
        // Only what changed is sent, so an unchanged Save would be
        // `{ row_version }` alone. It writes nothing instead (D-170; QA wave B
        // review, B-02 — the server answers such a body with a 200 no-op).
        const changes = codingRuleChanges(existing, body, isActive)
        if (nothingChanged(existing, changes)) {
          onSaved(NOTHING_CHANGED)
          return
        }
        await updateCodingRule(existing.id, changes)
      }
      onSaved(savedToast('rule', { wasNew: isNew, activeBefore: existing?.is_active, activeAfter: isActive }))
    } catch (e) {
      setSaveError(writeError(e, 'Could not save rule'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <AppDialog open={!!row} onOpenChange={(_, d) => !d.open && onClose()}>
      <DialogSurface ref={setDialogSurface}>
        <FrozenWhileClosed open={!!row}><DialogBody>
          <DialogTitle>{isNew ? 'Add Rule' : 'Edit Rule'}</DialogTitle>
          <DialogContent className={mergeClasses(df.stack, df.beforeActions)}>
            <ErrorBanner message={saveError} />
            {/* THE LIVE SENTENCE PREVIEW IS GONE (owner, 2026-09-11: "drop
                the gray box at the top"). Owner item 26's arrangement survives
                it and is what makes it redundant: the field LABELS are the
                clauses of that sentence, in the order they appear in it, so
                filling the dialog in top to bottom is already writing the rule
                out. Restating it in a panel above said the same words twice,
                and the row's own sentence in the list is unchanged — it is
                built in `CodingRulesManager`, not here, so nothing that reads a
                saved rule lost anything. */}
            <Field label="When a line item from">
              {/* "ANY VENDOR" IS THE PICKER'S FIRST OPTION (owner, 2026-09-11:
                  it "is randomly plopped onto the screen. I would expect it to
                  be the top line in the vendor picker"). It was a checkbox
                  above the picker, which made one question look like two
                  controls — and the checkbox governed whether the picker
                  rendered at all, so the two were never independent in the
                  first place.

                  G1 still holds: a rule that applies to everyone is a DECISION,
                  never a blank field. It is simply made by picking it. The
                  option is pinned to the top of every result page whose term it
                  matches, so it is reachable on the first open (empty term) and
                  by typing "any" — and is not offered against a term it has
                  nothing to do with, where it would read as a search result.

                  THE INVOICE MODAL'S SECOND LINE UNDER EACH VENDOR (owner,
                  2026-10-01, D-260: "anywhere that Rules offers a Vendor
                  picker, show the same three bits of context that we show
                  under a vendor name in the Vendor picker at Import Control").
                  The Vendor ID, the city and state, then the remittance city
                  and state, from `vendorOptionSubtext`, as on Vendors and
                  Guidance. This supersedes the 2026-09-11 ruling that dropped
                  the line here ("drop the help text under each option in the
                  dropdown, including 'Any vendor'"). "Any vendor" is not a
                  vendor and still has no second line.

                  THE SAME PICKER ON AN EDIT (owner, 2026-09-11: "let users
                  change the vendor on an existing coding rule"). It was a
                  read-only box, on the reasoning that a rule's vendor cannot be
                  repointed — but nothing server-side enforced that: the update
                  route has always accepted `vendor_id`, and the old "Any
                  vendor" checkbox could already null it. So the lock was a
                  claim the system never made. */}
              <div className={s.pickerRow}>
                <EntityPicker
                  value={anyVendor ? ANY_VENDOR_OPTION_VALUE : vendor}
                  placeholder="e.g. XEROX BUSINESS SOLUTIONS"
                  search={(t) =>
                    searchVendors(t).then<EntityOption[]>((vs) => [
                      ...(anyVendorMatches(t) ? [ANY_VENDOR_OPTION] : []),
                      ...vs.map((v) => ({
                        id: v.vendor_id,
                        primary: v.vendor_name || '(unnamed)',
                        secondary: vendorOptionSubtext(v) || undefined,
                      })),
                    ])
                  }
                  onSelect={(o) => {
                    if (o.id === ANY_VENDOR_ID) {
                      setAnyVendor(true)
                      setVendor({})
                      // "Any vendor" + "Any text" is the one combination that
                      // cannot be saved, and leaving the pair showing would
                      // disable Save with the explanation hidden — the hint
                      // that says why lives under the text box, which "Any
                      // text" does not render. Answer the question with the only
                      // answer that works, where the required field and its
                      // reason are both on screen. This is also what answers it
                      // at all for a new rule, which now starts with it open.
                      setScopeMode('specific')
                      return
                    }
                    setAnyVendor(false)
                    setVendor({ id: o.id, name: o.primary })
                  }}
                />
                {/* ALL OR NOTHING (owner, 2026-09-11): the Account and
                    Department pickers each clear with an X and this one did
                    not, so the same control answered the same way looked like
                    two different kinds of field. All — a vendor is as clearable
                    as the other two. */}
                {(anyVendor || vendor.id) && (
                  <Button
                    appearance="subtle"
                    size="small"
                    icon={<DismissRegular />}
                    aria-label="Clear Vendor"
                    onClick={() => {
                      setAnyVendor(false)
                      setVendor({})
                    }}
                  />
                )}
              </div>
            </Field>
            {/* TWO CONTROLS FOR ONE CLAUSE (owner, 2026-09-11): a dropdown
                that says whether this rule matches on the description at all,
                and — only when it does — the box saying on what. A lone text
                box could not distinguish "every line item from this vendor"
                from "I have not filled this in yet", and those are very
                different rules. A NEW RULE STARTS WITH NEITHER PICKED
                (owner, 2026-09-11) — see `scopeMode`'s own note above for why
                "Any text" was the wrong thing to pre-answer with. */}
            <Field label="contains">
              <Dropdown
                placeholder="e.g. specific text"
                // LOWER CASE (owner, 2026-09-14): "specific text" and "any
                // text" are not proper nouns, and title case made them read
                // as the names of two settings rather than as the two halves
                // of the sentence they sit inside.
                value={scopeMode === null ? '' : wantsScope ? 'specific text' : 'any text'}
                selectedOptions={scopeMode === null ? [] : [scopeMode]}
                onOptionSelect={(_, d) => {
                  if (d.optionValue === undefined) return
                  setScopeMode(d.optionValue === 'specific' ? 'specific' : 'any')
                }}
                listbox={{ className: listboxClass }}
                {...popupPositionProps(dialogSurface)}
              >
                {/* Disabled rather than absent with "any vendor": the rule
                    would code every line item the system has never seen, so
                    this is the one combination that cannot be saved — and a
                    disabled option says why it is unavailable where a missing
                    one would just look like a shorter list. */}
                <Option value="any" disabled={anyVendor} text="any text">
                  any text
                </Option>
                <Option value="specific" text="specific text">
                  specific text
                </Option>
              </Dropdown>
            </Field>
            {wantsScope && (
              /* "that includes", not "the text" (owner, 2026-09-14). It was the
                 one label on this dialog that named its own field instead of
                 continuing the sentence, so the reader hit a noun where every
                 other line hands them the next few words:

                   When a line item from XEROX BUSINESS SOLUTIONS contains
                   specific text that includes "paper", default that line
                   item's Account to Supplies and Department to
                   Administrative & General.

                 The hint stays: it is not an example - it says where the match
                 is applied, and in the any-vendor case why the field is
                 required at all. */
              <Field
                label="that includes"
                hint={
                  anyVendor
                    ? 'Required: a rule that names no vendor must say what to match on.'
                    : 'Matched anywhere in the line item’s description.'
                }
              >
                <FieldInput value={scope} onChange={(_, d) => setScope(d.value)} placeholder="e.g. paper" />
              </Field>
            )}
            {scopeEmptied ? (
              <Body1 className={s.dialogWarn}>{emptiedScopeWarning(anyVendor)}</Body1>
            ) : scopeChanged ? (
              <Body1 className={s.dialogWarn}>
                Matched as &ldquo;{normalizedScope}&rdquo; — line descriptions are compared with
                digits removed and punctuation collapsed.
              </Body1>
            ) : null}
            {/* "Account" (owner item 8, 2026-08-19 — formerly "What is it?"
                followed by the half's retired name), extended into the
                sentence's own clause by item 26, 2026-08-28. */}
            <Field label="default that line item's Account to">
              <div className={s.pickerRow}>
                <EntityPicker
                  value={account}
                  placeholder="e.g. Supplies"
                  search={(t) =>
                    searchAccounts(t).then<EntityOption[]>((accounts) =>
                      accounts.map((a) => ({ id: a.account_id, primary: a.account_name, secondary: a.account_id })),
                    )
                  }
                  onSelect={(o) => {
                    setAccount({ id: o.id, name: o.primary })
                    // Changing the Account invalidates whatever Department
                    // was chosen against the OLD one — the same reset the invoice
                    // modals apply (owner ruling 2026-08-10). This runs only from
                    // an actual pick, never from an effect keyed on
                    // `account.id`: an effect would also fire when the
                    // seeding effect above sets both halves for an EXISTING
                    // default, wiping the saved Department of every row merely
                    // opened for edit.
                    setDepartment({})
                  }}
                />
                {account.id && (
                  <Button
                    appearance="subtle"
                    size="small"
                    icon={<DismissRegular />}
                    aria-label="Clear Account"
                    onClick={() => {
                      setAccount({})
                      setDepartment({})
                    }}
                  />
                )}
              </div>
            </Field>
            {/* "Department" (owner item 8, 2026-08-19 — formerly "Who is it
                for?" followed by the half's retired name), extended into the
                sentence's own clause by item 26, 2026-08-28.

                HIDDEN UNTIL AN ACCOUNT IS CHOSEN (owner, 2026-09-11). The
                Department list is already scoped BY the Account — `optionsKey`
                below refetches it whenever the Account changes, and choosing an
                Account clears whatever Department was picked against the old
                one — so before an Account exists the picker can only offer a
                list it will immediately throw away. Hidden rather than
                disabled, which is how this dialog has always treated a control
                that does not apply yet.

                `|| department.id` is for DATA, not for the new rule: a
                Department-only row predates the Account-required rule (D-94)
                and can still exist, and hiding the only field that shows what
                such a row says would make it unreadable in its own editor. A
                new rule can never reach that state — `ready` requires an
                Account — so this widens nothing. */}
            {(account.id || department.id) && (
            <Field label="and Department to">
              <div className={s.pickerRow}>
                <EntityPicker
                  value={department}
                  placeholder="e.g. Administrative & General"
                  optionsKey={account.id ?? ''}
                  search={(t) =>
                    searchDepartments(t, account.id).then<EntityOption[]>((departments) =>
                      departments.map((d) => ({ id: d.department_id, primary: d.department_name, secondary: d.department_id })),
                    )
                  }
                  onSelect={(o) => setDepartment({ id: o.id, name: o.primary })}
                  // Emptied, the Department clears (D-295); its own button is beside it.
                  onClear={() => setDepartment({})}
                  clearButton={false}
                />
                {department.id && (
                  <Button
                    appearance="subtle"
                    size="small"
                    icon={<DismissRegular />}
                    aria-label="Clear Department"
                    onClick={() => setDepartment({})}
                  />
                )}
              </div>
            </Field>
            )}
            {departmentReach !== null && (
              <Body1 className={s.dialogWarn}>
                A rule that names only a Department applies only where that Department and
                whatever Account resolves make a real Code together.{' '}
                {department.name ?? department.id} pairs with {departmentReach.fits} of the{' '}
                {departmentReach.total} Accounts; on every other line item the rule contributes
                nothing.
              </Body1>
            )}
          </DialogContent>
          {!isNew && (
            <DialogActions position="start">
              <StatusToggle value={isActive} onChange={setIsActive} />
            </DialogActions>
          )}
          <DialogActions>
            <Button appearance="secondary" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button appearance="primary" disabled={!ready || saving} onClick={() => void save()}>
              Save
            </Button>
          </DialogActions>
        </DialogBody></FrozenWhileClosed>
      </DialogSurface>
    </AppDialog>
  )
}

// ---- Companies → Uploaders (formerly Domains, and before that References → Senders)
// This tab shows four facts per entry: `intake_senders` (may this address or
// domain email documents in), `in_app_upload_blocks` (may it NOT upload in
// the app, D-394), `company_email_domains` (which company its
// uploads and emails file to — a full address may route too since D-379, and
// wins over its domain) and `sender_format_defaults` (whether its documents'
// format is set rather than guessed at extraction, D-379). They stay three
// tables, read as three lists and joined for display on the exact stored
// string (`mergeSenderEntries`, `../lib/senders`), and written as ONE request —
// `PUT /sender-rules`, every row in one server transaction (G-01, D-275). The
// routing rung governs IN-APP uploads as well as email — `uploads.ts` sets
// `source` from the signed-in principal for a non-service caller,
// `extract.ts` copies it to `invoices.uploader_email`, and `matchingLadder.ts`
// matches that address, then its domain, against this same table; the format
// default is read by `extract.ts` from the same `source`.

/**
 * A STATUS CHANGE, written: one `PUT /sender-rules` — the one write the
 * editor's Status and the bulk strip share (B-01). It THROWS when there is
 * nothing to write, so neither caller can toast a change that did not happen.
 */
async function writeListing(e: SenderEntry, active: boolean): Promise<void> {
  const request = statusRequest(e, active)
  if (request === null) {
    throw new Error(`There is nothing on record to make ${active ? 'Active' : 'Inactive'} for ${e.key}.`)
  }
  await saveSenderRule(request)
}

// ONE SENTENCE AND A STATUS, like every other Rules table (owner, 2026-09-14),
// in the owner's own words since 2026-10-19 (D-379) — `uploaderRuleSentence`
// in `../lib/senders`, which the editor's clauses follow in order.

function SendersManager() {
  const toast = useToast()
  const [entries, setEntries] = useState<SenderEntry[] | null>(null)
  const [companyNames, setCompanyNames] = useState<ReadonlyMap<string, string>>(new Map())
  /** The ACTIVE companies, in the order the server returns them — the Routing
      dropdown's options (owner item 25, 2026-08-28). Kept alongside
      `companyNames` rather than derived from it: that map is a
      company_id -> name lookup over EVERY company including retired ones,
      because a retired company still has to render its name on a rule that
      already points at it. Neither can do the other's job. */
  const [activeCompanies, setActiveCompanies] = useState<{ id: string; name: string }[]>([])
  const [status, setStatus] = useState<StatusFilter>(DEFAULT_STATUS_FILTER)
  const [error, setError] = useState<string | null>(null)
  const [term, setTerm] = useState('')
  const [edit, setEdit] = useState<SenderEntry | 'new' | null>(null)

  const load = useCallback(async () => {
    setError(null)
    try {
      const [senders, domains, formats, blocks, companies] = await Promise.all([
        loadIntakeSenders(),
        loadCompanyEmailDomains(),
        loadSenderFormatDefaults(),
        loadInAppUploadBlocks(),
        loadCompaniesForManagement(),
      ])
      setEntries(mergeSenderEntries(senders, domains, formats, blocks))
      setCompanyNames(new Map(companies.map((c) => [c.company_id, c.company_name])))
      setActiveCompanies(
        companies
          .filter((c) => c.is_active !== false)
          .map((c) => ({ id: c.company_id, name: c.company_name })),
      )
    } catch (e) {
      setError(errorMessage(e))
      setEntries([])
    }
  }, [])
  useEffect(() => {
    void load()
  }, [load])

  const visible = useMemo(() => {
    const q = term.trim().toLowerCase()
    return (entries ?? [])
      .filter((e) => matchesStatus(status, entryIsActive(e)))
      .filter((e) => {
        if (!q) return true
        if (e.key.toLowerCase().includes(q)) return true
        const name = routedCompanyName(e, companyNames)
        return !!name && name.toLowerCase().includes(q)
      })
  }, [entries, status, term, companyNames])

  const sentence = useCallback(
    (e: SenderEntry) => uploaderRuleSentence(e, companyNames),
    [companyNames],
  )

  if (entries === null) return <Spinner label="Loading senders…" />

  return (
    <div>
      {error && <ErrorBanner message={error} />}
      {/* This table had no heading of its own until 2026-09-11 (owner) — the
          aliases manager stacked below it did, so the tab read as one unnamed
          table followed by one named one. Both are named now.

          Its intro paragraph is gone with the rename, to the tab's subheader,
          which describes both managers. That paragraph (owner item 25,
          2026-08-28) had itself replaced three explaining the two facts, the
          domain-only routing rule, the billed-AI-read cost of intake and the
          pinned bootstrap floor's separate Graph path. What survives of all of
          it lives where it is actionable: the rule's own sentence and editor,
          and `docs/app.md`. The domain-only routing rule it once carried is
          gone (D-379). */}
      {/* The page's own tools live in the header's top-right cluster since
          2026-09-14 (owner) — see `components/ScreenActions.tsx`. */}
      <ScreenActions>
        <Tooltip content="Add Rule" relationship="label">
          <Button appearance="secondary" icon={<AddRegular />} onClick={() => setEdit('new')} />
        </Tooltip>
        <ListFilters
          status={status}
          onStatusChange={setStatus}
          term={term}
          onTermChange={setTerm}
          searchPlaceholder="Search address, domain, or company…"
          ariaLabel="Uploader Rules"
        />
      </ScreenActions>
      <RuleTable
        rows={visible}
        emptyText={(entries ?? []).length === 0 ? 'No senders to show.' : 'No senders match.'}
        ariaLabel="Uploader rules"
        selectAllLabel="uploader rules"
        keyOf={senderKey}
        isActive={entryIsActive}
        sentence={sentence}
        // THE STORED ROWS, NEVER A NEW ONE: `writeListing` sets `is_active` on
        // whichever of the entry's three rows exist (`lib/senders.ts`), so Mark
        // Active never invents a row that was not there.
        write={writeListing}
        noun="rule"
        onEdit={setEdit}
        onMarked={(m: string) => {
          toast(m)
          void load()
        }}
        onError={setError}
      />
      <SenderDialog
        row={edit}
        entries={entries}
        companyNames={companyNames}
        activeCompanies={activeCompanies}
        onClose={() => setEdit(null)}
        onSaved={(message, isError) => {
          setEdit(null)
          toast(message, isError ? 'error' : 'success')
          void load()
        }}
      />
    </div>
  )
}

function SenderDialog({
  row,
  entries,
  companyNames,
  activeCompanies,
  onClose,
  onSaved,
}: {
  row: SenderEntry | 'new' | null
  /** Every rule on the tab, for the "Already in use" warning before Save (A-02). */
  entries: readonly SenderEntry[] | null
  companyNames: ReadonlyMap<string, string>
  /** The routing dropdown's options — active companies only. */
  activeCompanies: readonly { id: string; name: string }[]
  onClose: () => void
  /** Full success, OR a partial write (`isError` true) that already changed
      something — either way the caller closes, toasts and reloads. */
  onSaved: (message: string, isError?: boolean) => void
}) {
  const s = useStyles()
  const df = useDialogFormStyles()
  // THIS DIALOG'S OWN REFUSAL (QA wave A, 2026-09-22, A-01). It used to go
  // to the manager's screen banner, which renders behind the modal.
  const [saveError, setSaveError] = useDialogError(row)
  const isNew = row === 'new'
  const existing = row && row !== 'new' ? row : null
  /** See `VendorDefaultDialog` above for why the surface is resolved here. */
  const [dialogSurface, setDialogSurface] = useState<HTMLElement | null>(null)
  const listboxClass = usePopupListboxClass()
  /** WHO THE RULE IS ABOUT, the sentence's first choice (owner, 2026-10-19 —
      D-379): "a specific user" or "a general domain". Unanswered on a new
      rule, like intake; an edit shows the stored key's kind, locked, because
      the key itself cannot change. */
  const [kind, setKind] = useState<SenderKeyKind | null>(null)
  const [key, setKey] = useState('')
  /** `null` is "not answered yet", and it is where a NEW rule starts (owner,
      2026-09-14): a dropdown that opens on an answer has answered a question
      for the reader. An EDIT seeds the stored value. */
  const [intakeOn, setIntakeOn] = useState<boolean | null>(null)
  /** In-app uploads (D-394), unanswered on a new rule like emailed uploads. */
  const [inAppOn, setInAppOn] = useState<boolean | null>(null)
  const [company, setCompany] = useState<{ id?: string; name?: string }>({})
  /** The format clause (D-379): `null` is "is interpreted", the default. */
  const [format, setFormat] = useState<DocumentForm | null>(null)
  /** THE ENTRY'S LISTING, as every other rule editor on this screen offers it
      (QA wave A, A-02). What it writes is what the bulk strip writes:
      `is_active` on WHICHEVER of the entry's stored rows exist. */
  const [isActive, setIsActive] = useState(true)
  const [saving, setSaving] = useState(false)
  const [confirmOffs, setConfirmOffs] = useState<string[] | null>(null)

  useEffect(() => {
    if (!row) return
    if (row === 'new') {
      setKind(null)
      setKey('')
      setIntakeOn(null)
      setInAppOn(null)
      setCompany({})
      setFormat(null)
      setIsActive(true)
      return
    }
    setKind(senderKeyKind(row.key))
    setKey(row.key)
    setIsActive(entryIsActive(row))
    // THE RULE's clauses — on an Inactive entry, the rows Active would put
    // back (B-01).
    setIntakeOn(ruleIntake(row) !== null)
    setInAppOn(ruleInAppAllowed(row))
    const routing = ruleRouting(row)
    setCompany(
      routing ? { id: routing.company_id, name: routedCompanyName(row, companyNames) ?? routing.company_id } : {},
    )
    setFormat(ruleFormat(row))
    // Seeded from `row` alone — `companyNames` is read once at seed time to
    // resolve the label; a later re-render of the map should not retroactively
    // rewrite what the dialog is showing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row])

  const normalizedKey = isNew ? normalizeSenderKey(key) : existing?.key ?? ''
  /** The key is not what its kind says: an address under "a general domain", or
      a domain under "a specific user" — the route's own refusal, said before Save. */
  const misfit = isNew && kind !== null && normalizedKey !== '' && !senderKeyFits(kind, normalizedKey)
  /** "Will be stored as …" — any change normalization makes, case and spaces
      included (QA 2026-10-04 A-01). */
  const storedAs = isNew && !misfit ? storedKeyNotice(key) : null
  /** The Active rule a new key would duplicate (A-02) — warned before Save,
      as Vendors and Aliases do, in the server's own sentence. */
  const takenBy = isNew && !misfit ? activeEntryFor(entries, key) : null
  /** An Inactive entry, left Inactive. Its clauses are shown, not editable:
      a clause edit would write ACTIVE rows under an Inactive rule, so the
      reader makes it Active first (B-01). */
  const dormantEntry = !!existing && !entryIsActive(existing)
  // A rule needs an answer that does something: emailed uploads allowed,
  // in-app uploads NOT allowed (D-394), an assumed company, or an assumed
  // format. An edit that leaves none is a rule that does nothing — the server
  // refuses it; Status → Inactive is the way to stop a rule.
  const doesSomething = intakeOn === true || inAppOn === false || !!company.id || format !== null
  const ready = isNew
    ? kind !== null && normalizedKey !== '' && !misfit && takenBy === null && inAppOn !== null && doesSomething
    : !!existing && (dormantEntry || isActive !== entryIsActive(existing) || doesSomething)

  /** What this save would stop, so a turn-off is confirmed rather than silent. */
  function turnOffMessages(): string[] {
    const msgs: string[] = []
    if (existing?.intake && intakeOn === false) {
      msgs.push(`Stop accepting emailed documents from ${existing.key}?`)
    }
    if (existing && existing.inApp === null && inAppOn === false) {
      msgs.push(`Stop in-app uploads by users with the ${SENDER_KIND_WORDS[existing.isDomain ? 'domain' : 'address']} ${existing.key}?`)
    }
    if (existing?.routing && !company.id) {
      const name = routedCompanyName(existing, companyNames) ?? existing.routing.company_id
      msgs.push(`Stop filing documents from ${existing.key} to ${name}?`)
    }
    if (existing?.format && format === null) {
      msgs.push(`Stop recording documents from ${existing.key} as ${existing.format.document_form}?`)
    }
    return msgs
  }

  /** The reader has moved Status, so this save is a retire or a reactivate.
      EXCLUSIVE of the clause edits below — those controls are disabled while it
      is true, so nothing chosen can be silently dropped by it. */
  const listingChanged = !!existing && isActive !== entryIsActive(existing)
  const clausesLocked = listingChanged || dormantEntry

  async function saveListing() {
    if (!existing) return
    setSaving(true)
    try {
      // `writeListing` — the exact write the bulk strip performs (B-01).
      await writeListing(existing, isActive)
    } catch (e) {
      setSaving(false)
      setSaveError(writeError(e, isActive ? 'Could not make the rule Active' : 'Could not make the rule Inactive'))
      return
    }
    setSaving(false)
    onSaved(statusToast('rule', isActive))
  }

  async function doSave() {
    if (listingChanged) {
      await saveListing()
      return
    }
    if (dormantEntry) {
      // Inactive, and Status not moved: nothing to write (B-01).
      onSaved(NOTHING_CHANGED)
      return
    }
    setSaving(true)
    // ONE REQUEST, EVERY ROW (G-01, D-275): the server writes all three sides
    // in one transaction, so a failure saves none of them.
    let changed: boolean
    try {
      const result = await saveSenderRule(
        clauseRequest(normalizedKey, existing, {
          kind: kind ?? senderKeyKind(normalizedKey),
          intakeAllowed: intakeOn === true,
          inAppAllowed: inAppOn !== false,
          companyId: company.id ?? null,
          documentForm: format,
        }),
      )
      changed = result.changed
    } catch (e) {
      setSaving(false)
      setSaveError(writeError(e, 'Could not save the rule'))
      return
    }
    setSaving(false)
    if (!changed) {
      onSaved(NOTHING_CHANGED)
      return
    }
    onSaved(savedToast('rule', { wasNew: isNew }))
  }

  function attemptSave() {
    const offs = listingChanged ? [] : turnOffMessages()
    if (offs.length > 0) {
      setConfirmOffs(offs)
      return
    }
    void doSave()
  }

  const formatValue = format ?? 'interpreted'

  return (
    <>
      <AppDialog open={!!row} onOpenChange={(_, d) => !d.open && onClose()}>
        <DialogSurface ref={setDialogSurface}>
          <FrozenWhileClosed open={!!row}><DialogBody>
            <DialogTitle>{isNew ? 'Add Rule' : 'Edit Rule'}</DialogTitle>
            <DialogContent className={mergeClasses(df.stack, df.beforeActions)}>
              <ErrorBanner message={saveError} />
              {/* THE RULE'S OWN TWO SENTENCES, clause by clause, in the owner's
                  words (2026-10-22 — D-394; one sentence under D-379):

                    For users with the [address | domain] [the address or
                    domain] emailed uploads are [allowed | not allowed] and
                    in-app uploads are [allowed | not allowed].
                    For all allowed uploads, the invoice's Company [is assumed
                    to be <company> | is interpreted] and format [is assumed
                    to be electronic | is assumed to be paper | is interpreted]

                  The second sentence starts at its own field. No hint
                  under any control: a key that is not what its kind says is
                  refused in the route's own words, and the stored key and the
                  duplicate are said as before (QA 2026-10-04 A-01, A-02). */}
              <Field label="For users with the">
                {/* LOCKED ON AN EDIT, in the one look every locked field has
                    (owner item 2, 2026-08-28): the key cannot change, so
                    neither can what kind of key it is. */}
                {!isNew ? (
                  <FieldInput value={kind === null ? '' : SENDER_KIND_WORDS[kind]} readOnly appearance="filled-darker" />
                ) : (
                  <Dropdown
                    placeholder="e.g. address"
                    value={kind === null ? '' : SENDER_KIND_WORDS[kind]}
                    selectedOptions={kind === null ? [] : [kind]}
                    onOptionSelect={(_, d) => {
                      if (d.optionValue === 'address' || d.optionValue === 'domain') setKind(d.optionValue)
                    }}
                    listbox={{ className: listboxClass }}
                    {...popupPositionProps(dialogSurface)}
                  >
                    <Option value="address" text={SENDER_KIND_WORDS.address}>
                      {SENDER_KIND_WORDS.address}
                    </Option>
                    <Option value="domain" text={SENDER_KIND_WORDS.domain}>
                      {SENDER_KIND_WORDS.domain}
                    </Option>
                  </Dropdown>
                )}
              </Field>
              <Field>
                {isNew ? (
                  <FieldInput
                    aria-label={kind === 'domain' ? 'Domain' : 'Address'}
                    value={key}
                    onChange={(_, d) => setKey(d.value)}
                    placeholder={kind === 'domain' ? 'e.g. @seniorlifejohnstown.com' : 'e.g. SLJDocumentScan@seniorlifejohnstown.com'}
                  />
                ) : (
                  <FieldInput
                    aria-label={kind === 'domain' ? 'Domain' : 'Address'}
                    value={existing?.key ?? ''}
                    readOnly
                    appearance="filled-darker"
                  />
                )}
              </Field>
              {misfit && kind !== null && <Body1 className={s.dialogWarn}>{senderKeyMisfit(kind)}</Body1>}
              {storedAs !== null && takenBy === null && (
                <Body1 className={s.dialogWarn}>Will be stored as &ldquo;{storedAs}&rdquo;.</Body1>
              )}
              {takenBy !== null && <Body1 className={s.dialogWarn}>{senderRuleTaken(takenBy.key)}</Body1>}
              <Field label="emailed uploads are">
                <Dropdown
                  // Disabled while Status is being moved (QA wave A, A-02), and
                  // on an Inactive entry (B-01): see `clausesLocked`.
                  disabled={clausesLocked}
                  placeholder="e.g. not allowed"
                  value={intakeOn === null ? '' : intakeOn ? ALLOWED_WORDS.yes : ALLOWED_WORDS.no}
                  selectedOptions={intakeOn === null ? [] : [intakeOn ? 'yes' : 'no']}
                  onOptionSelect={(_, d) => {
                    if (d.optionValue === undefined) return
                    setIntakeOn(d.optionValue === 'yes')
                  }}
                  listbox={{ className: listboxClass }}
                  {...popupPositionProps(dialogSurface)}
                >
                  <Option value="yes" text={ALLOWED_WORDS.yes}>
                    {ALLOWED_WORDS.yes}
                  </Option>
                  <Option value="no" text={ALLOWED_WORDS.no}>
                    {ALLOWED_WORDS.no}
                  </Option>
                </Dropdown>
              </Field>
              {/* IN-APP UPLOADS (owner, 2026-10-22 — D-394): "not allowed" disables
                  the Upload button for these users, and the upload routes
                  refuse them. "allowed" adds no block, as no rule does; a user still
                  needs a role or appointment to upload. */}
              <Field label="and in-app uploads are">
                <Dropdown
                  disabled={clausesLocked}
                  placeholder="e.g. allowed"
                  value={inAppOn === null ? '' : inAppOn ? ALLOWED_WORDS.yes : ALLOWED_WORDS.no}
                  selectedOptions={inAppOn === null ? [] : [inAppOn ? 'yes' : 'no']}
                  onOptionSelect={(_, d) => {
                    if (d.optionValue === undefined) return
                    setInAppOn(d.optionValue === 'yes')
                  }}
                  listbox={{ className: listboxClass }}
                  {...popupPositionProps(dialogSurface)}
                >
                  <Option value="yes" text={ALLOWED_WORDS.yes}>
                    {ALLOWED_WORDS.yes}
                  </Option>
                  <Option value="no" text={ALLOWED_WORDS.no}>
                    {ALLOWED_WORDS.no}
                  </Option>
                </Dropdown>
              </Field>
              {/* ROUTING FOR AN ADDRESS TOO (owner, 2026-10-19 — D-379:
                  "Routing may be specific to a user"). An exact address wins
                  over its domain; no warning, by the owner's ruling. */}
              <Field label="For all allowed uploads, the invoice’s Company">
                <Dropdown
                  disabled={clausesLocked}
                  placeholder="e.g. is assumed to be SeniorLIFE Johnstown"
                  // "None" is a real stored answer; a NEW rule nobody has keyed
                  // yet shows the example instead.
                  value={
                    company.id
                      ? companyWords(company.name ?? company.id)
                      : isNew && normalizedKey === ''
                        ? ''
                        : companyWords(null)
                  }
                  selectedOptions={[company.id ?? '']}
                  onOptionSelect={(_, d) => {
                    if (d.optionValue === undefined) return
                    if (d.optionValue === '') {
                      setCompany({})
                      return
                    }
                    setCompany({ id: d.optionValue, name: activeCompanies.find((c) => c.id === d.optionValue)?.name ?? d.optionValue })
                  }}
                  listbox={{ className: listboxClass }}
                  {...popupPositionProps(dialogSurface)}
                >
                  <Option value="" text={companyWords(null)}>
                    {companyWords(null)}
                  </Option>
                  {activeCompanies.map((c) => (
                    <Option key={c.id} value={c.id} text={companyWords(c.name)}>
                      {companyWords(c.name)}
                    </Option>
                  ))}
                </Dropdown>
              </Field>
              {/* THE FORMAT DEFAULT (owner, 2026-10-19 — D-379): a scan-to-email
                  address marked paper is recorded as paper whatever pass 1 guesses. "is
                  interpreted" is the default: pass 1's guess stands (D-378). */}
              <Field label="and format">
                <Dropdown
                  disabled={clausesLocked}
                  value={FORMAT_WORDS[formatValue]}
                  selectedOptions={[formatValue]}
                  onOptionSelect={(_, d) => {
                    if (d.optionValue === DOCUMENT_FORM.Electronic || d.optionValue === DOCUMENT_FORM.Paper) {
                      setFormat(d.optionValue)
                    } else if (d.optionValue === 'interpreted') {
                      setFormat(null)
                    }
                  }}
                  listbox={{ className: listboxClass }}
                  {...popupPositionProps(dialogSurface)}
                >
                  <Option value={DOCUMENT_FORM.Electronic} text={FORMAT_WORDS.Electronic}>
                    {FORMAT_WORDS.Electronic}
                  </Option>
                  <Option value={DOCUMENT_FORM.Paper} text={FORMAT_WORDS.Paper}>
                    {FORMAT_WORDS.Paper}
                  </Option>
                  <Option value="interpreted" text={FORMAT_WORDS.interpreted}>
                    {FORMAT_WORDS.interpreted}
                  </Option>
                </Dropdown>
              </Field>
            </DialogContent>
            {!isNew && (
              <DialogActions position="start">
                <StatusToggle value={isActive} onChange={setIsActive} />
              </DialogActions>
            )}
            <DialogActions>
              <Button appearance="secondary" onClick={onClose} disabled={saving}>
                Cancel
              </Button>
              <Button appearance="primary" disabled={!ready || saving} onClick={attemptSave}>
                Save
              </Button>
            </DialogActions>
          </DialogBody></FrozenWhileClosed>
        </DialogSurface>
      </AppDialog>
      <ConfirmDialog
        open={confirmOffs !== null}
        title="Confirm Change"
        body={(confirmOffs ?? []).join(' ')}
        confirmLabel="Continue"
        busy={saving}
        onConfirm={() => {
          setConfirmOffs(null)
          void doSave()
        }}
        onCancel={() => setConfirmOffs(null)}
      />
    </>
  )
}

// ---- Vendors (vendor aliases, owner item 10b, 2026-08-13) -------------------
//
// A rule reads back as a full sentence, no source annotation — same reasoning
// as the Codes tab: the Source filter (Rules or Memory, D-367) says where a
// rule came from.
//
//   When an invoice arrives from "xerox", attribute the invoice to XEROX
//   BUSINESS SOLUTIONS.
//
// Backed by `vendor_aliases` (extended with a writer, not a new table). A
// match is a DETERMINISTIC OVERRIDE of vendor identity, ahead of vendor
// precedent (`docs/ai.md` §3.2) — stronger than a Codes-tab default,
// which pass 2 can still weigh against the document. `alias_name` is
// normalized SERVER-SIDE (trimmed, internal whitespace collapsed); the
// preview below uses the server's own `normalizeAliasName` (`@ap/shared`),
// but the server never trusts it.



function VendorAliasesManager() {
  const toast = useToast()
  const [all, setAll] = useState<VendorAlias[] | null>(null)
  const [status, setStatus] = useState<StatusFilter>(DEFAULT_STATUS_FILTER)
  const [source, setSource] = useState<string[]>(DEFAULT_RULE_SOURCE_FILTER)
  const [error, setError] = useState<string | null>(null)
  const [edit, setEdit] = useState<VendorAlias | 'new' | null>(null)
  const [term, setTerm] = useState('')

  const load = useCallback(async () => {
    setError(null)
    try {
      setAll(await loadVendorAliases())
    } catch (e) {
      setError(errorMessage(e))
      setAll([])
    }
  }, [])
  useEffect(() => {
    void load()
  }, [load])

  // `vendor_aliases.vendor_name` is a denormalized cache written at create
  // time (`docs/schema.md`), not a live lookup — but it always has a value
  // to fall back to, unlike a coding rule, which is why this tab never
  // needed the id-to-name search `loadVendorNames` used to run here.
  const vendorName = useCallback((a: VendorAlias) => a.vendor_name || a.vendor_id, [])

  /** The rule as a full, true sentence — the whole point of the row. */
  const sentence = useCallback(
    (a: VendorAlias): string =>
      closeSentence(`When an invoice arrives from "${a.alias_name}", attribute the invoice to ${vendorName(a)}`),
    [vendorName],
  )

  const visible = useMemo(() => {
    const q = term.trim().toLowerCase()
    const haystack = (a: VendorAlias) => [a.alias_name, a.vendor_id, vendorName(a)].join(' ').toLowerCase()
    return (all ?? [])
      .filter((a) => matchesStatus(status, a.is_active))
      .filter((a) => matchesRuleSource(source, a.source === VENDOR_ALIAS_SOURCE.Learned))
      .filter((a) => !q || haystack(a).includes(q))
  }, [all, status, source, term, vendorName])

  if (all === null) return <Spinner label="Loading vendor rules…" />

  return (
    <div>
      {error && <ErrorBanner message={error} />}
      {/* The sentence that stood here (owner item 24, 2026-08-28) is the tab's
          subheader since 2026-09-11 — moved, not deleted. */}
      {/* The page's own tools live in the header's top-right cluster since
          2026-09-14 (owner) — see `components/ScreenActions.tsx`. */}
      <ScreenActions>
        <Tooltip content="Add Rule" relationship="label">
          <Button appearance="secondary" icon={<AddRegular />} onClick={() => setEdit('new')} />
        </Tooltip>
        <ListFilters
          status={status}
          onStatusChange={setStatus}
          term={term}
          onTermChange={setTerm}
          searchPlaceholder="Search text or vendor…"
          ariaLabel="Vendor Name Rules"
          selects={[sourceSelect(source, setSource)]}
        />
      </ScreenActions>
      <RuleTable
        rows={visible}
        emptyText={(all ?? []).length === 0 ? 'No rules to show.' : 'No rules match.'}
        ariaLabel="Vendor name rules"
        selectAllLabel="vendor name rules"
        keyOf={rowId}
        isActive={rowIsActive}
        sentence={sentence}
        write={writeVendorAliasActive}
        noun="rule"
        onEdit={setEdit}
        onMarked={(m: string) => {
          toast(m)
          void load()
        }}
        onError={setError}
      />
      <VendorAliasDialog
        row={edit}
        siblings={all ?? []}
        vendorName={vendorName}
        onClose={() => setEdit(null)}
        onSaved={(message) => {
          setEdit(null)
          toast(message)
          void load()
        }}
      />
    </div>
  )
}

function VendorAliasDialog({
  row,
  siblings,
  vendorName,
  onClose,
  onSaved,
}: {
  row: VendorAlias | 'new' | null
  /** Every rule the manager has loaded, so the form can say that an alias is
      taken WHILE IT IS BEING TYPED. The server refuses the same thing
      (`assertAliasTextFree`) and is the actual boundary; this is so nobody
      fills the form in, presses Save and is told no. */
  siblings: readonly VendorAlias[]
  /** Resolver from `VendorAliasesManager`'s label loader — used only to seed an
      EDIT with a label instead of a raw vendor id; a fresh pick already carries
      its own label via `EntityPicker`'s `onSelect`. */
  vendorName: (a: VendorAlias) => string
  onClose: () => void
  /** The toast, from `savedToast` — the dialog knows whether Status moved. */
  onSaved: (message: string) => void
}) {
  const s = useStyles()
  const df = useDialogFormStyles()
  // THIS DIALOG'S OWN REFUSAL (QA wave A, 2026-09-22, A-01). It used to go
  // to the manager's screen banner, which renders behind the modal.
  const [saveError, setSaveError] = useDialogError(row)
  const isNew = row === 'new'
  const existing = row && row !== 'new' ? row : null
  const [aliasText, setAliasText] = useState('')
  const [vendor, setVendor] = useState<{ id?: string; name?: string }>({})
  const [isActive, setIsActive] = useState(true)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!row) return
    if (row === 'new') {
      setAliasText('')
      setVendor({})
      setIsActive(true)
      return
    }
    setAliasText(row.alias_name)
    setVendor({ id: row.vendor_id, name: vendorName(row) })
    setIsActive(row.is_active)
    // `row` is the only thing this effect seeds from — `vendorName` is a
    // memoized callback from the manager and stable across a render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row])

  const normalizedAlias = normalizeAliasName(aliasText)
  const aliasChanged = aliasText.trim() !== '' && normalizedAlias !== aliasText.trim()

  // ONE ACTIVE RULE PER ALIAS (owner, 2026-09-15). Compared the way the server
  // and the matching ladder compare it — trimmed, case-insensitive. A RETIRED
  // rule is not in the way, which is why `is_active` is part of the test: this
  // is also what refuses the Active switch on a retired rule whose text
  // somebody else has taken since.
  const takenBy =
    normalizedAlias === '' || !isActive
      ? null
      : (siblings.find(
          (a) =>
            a.is_active &&
            a.id !== existing?.id &&
            a.alias_name.trim().toLowerCase() === normalizedAlias.trim().toLowerCase(),
        ) ?? null)

  const ready = normalizedAlias !== '' && !!vendor.id && takenBy === null

  async function save() {
    if (!ready || !vendor.id) return
    setSaving(true)
    try {
      // `evidence` is no longer written from this screen (owner item 24 — no
      // notes section). The column stays: it is the provenance note the
      // matching ladder's own alias writers set, and clearing it here would
      // erase theirs. Omitting the key leaves it untouched on a PATCH (§0.7).
      if (isNew) {
        await createVendorAlias({ alias_name: normalizedAlias, vendor_id: vendor.id })
      } else if (existing) {
        const next = { alias_name: normalizedAlias, vendor_id: vendor.id, is_active: isActive }
        // A Save that changed nothing writes nothing (D-170).
        if (nothingChanged(existing, next)) {
          onSaved(NOTHING_CHANGED)
          return
        }
        await updateVendorAlias(existing.id, { ...next, row_version: existing.row_version })
      }
      onSaved(savedToast('rule', { wasNew: isNew, activeBefore: existing?.is_active, activeAfter: isActive }))
    } catch (e) {
      setSaveError(writeError(e, 'Could not save rule'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <AppDialog open={!!row} onOpenChange={(_, d) => !d.open && onClose()}>
      <DialogSurface>
        <FrozenWhileClosed open={!!row}><DialogBody>
          <DialogTitle>{isNew ? 'Add Rule' : 'Edit Rule'}</DialogTitle>
          <DialogContent className={mergeClasses(df.stack, df.beforeActions)}>
            <ErrorBanner message={saveError} />
            {/* THE FIELD LABELS ARE THE CLAUSES OF THE RULE'S OWN SENTENCE
                (owner, 2026-09-14: every Add Rule pop-up "should take data in
                plain language, like they later present it, using their
                placeholder help text to provide example"), in the order they
                appear in it:

                  When an invoice arrives from "xerox", attribute the invoice
                  to XEROX BUSINESS SOLUTIONS.

                which is verbatim the sentence the saved rule reads back as in
                the list. The Codes dialog has been arranged this way since
                owner item 26 (2026-08-28); this is the same arrangement on the
                other four. The hint that stood here said the comparison is
                trimmed and case-insensitive AND gave "xerox" as its example —
                the example is the placeholder's job now, and what remained was
                a sentence about normalization above a field nobody had typed
                in yet. The "Stored as …" line below still says it, at the
                moment it actually bites. */}
            <Field label="When an invoice arrives from">
              <FieldInput value={aliasText} onChange={(_, d) => setAliasText(d.value)} placeholder="e.g. xerox" />
            </Field>
            {aliasChanged && takenBy === null && (
              <Body1 className={s.dialogWarn}>
                Stored as &ldquo;{normalizedAlias}&rdquo; — leading, trailing and repeated spaces are
                collapsed.
              </Body1>
            )}
            {takenBy !== null && (
              <Body1 className={s.dialogWarn}>
                Already in use: &ldquo;{takenBy.alias_name}&rdquo; attributes invoices to{' '}
                {vendorName(takenBy)}. Make that rule Inactive, or edit it, rather than adding a second
                one — two rules naming one printed name would leave both doing nothing.
              </Body1>
            )}
            <Field label="attribute the invoice to">
              <EntityPicker
                value={vendor}
                placeholder="e.g. XEROX BUSINESS SOLUTIONS"
                search={(t) =>
                  searchVendors(t).then<EntityOption[]>((vs) =>
                    vs.map((v) => ({
                      id: v.vendor_id,
                      primary: v.vendor_name || '(unnamed)',
                      // The invoice's Vendor picker's line (D-260): the Vendor ID,
                      // the city and state, the remittance city and state (D-241).
                      secondary: vendorOptionSubtext(v) || undefined,
                    })),
                  )
                }
                onSelect={(o) => setVendor({ id: o.id, name: o.primary })}
              />
            </Field>
          </DialogContent>
          {!isNew && (
            <DialogActions position="start">
              <StatusToggle value={isActive} onChange={setIsActive} />
            </DialogActions>
          )}
          <DialogActions>
            <Button appearance="secondary" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button appearance="primary" disabled={!ready || saving} onClick={() => void save()}>
              Save
            </Button>
          </DialogActions>
        </DialogBody></FrozenWhileClosed>
      </DialogSurface>
    </AppDialog>
  )
}

// ---- Guidance (the vendor extraction-guidance training loop, docs/ai.md §9) --
//
// Two ways a row gets here since 2026-09-11 (M6): the nightly worker, which
// authors from a repeated correction pattern, and Add Guidance on this tab,
// which takes a vendor and a sentence and nothing else. Guidance is keyed on
// the VENDOR (M1) — a person picks one from the master and the as-printed
// spellings are the system's problem, not the author's — and a vendor holds
// ONE active row (M9), so adding a second REPLACES the first as a supersede
// (M7): the old version is retired into the audit chain, never overwritten.
// An existing row is still only SUPERSEDED (a new version, never an in-place
// content edit) or RETIRED — the only "delete" `ap_app`'s no-DELETE posture
// allows.

// The mirrored field/symptom vocabularies and their label maps that used to
// live here are gone (owner item 27, 2026-08-28). Guidance is vendor-scoped
// free text: nothing on this tab picks a field, picks a symptom, or renders
// either one. Both columns still exist and are still written — by the
// authoring worker, which uses them to scope its own Bedrock call and to
// decide when a vendor qualifies for one at all (`docs/ai.md` §9) — and a
// human supersede carries them forward untouched, server-side.


/**
 * The vendor a guidance row is for. Since M1 the row is KEYED on the vendor,
 * so the vendor's own name is what the tab shows; `extracted_vendor_value` is
 * the fallback for a legacy row keyed on what a letterhead printed, and the
 * raw id is the last resort so a row is never nameless.
 */
function guidanceVendorLabel(g: VendorExtractionGuidance): string {
  return g.vendor_name || g.extracted_vendor_value || g.vendor_id || '(no vendor)'
}

/** Everything that names the vendor on a row — all three are what someone
    typing into the search box might have in mind. */
function guidanceVendorHaystack(g: VendorExtractionGuidance): string {
  return [g.vendor_name, g.extracted_vendor_value, g.vendor_id].filter(Boolean).join(' ')
}

/**
 * ONE SENTENCE AND A STATUS, like every other Rules table (owner, 2026-09-14):
 *
 *   When an invoice arrives from XEROX BUSINESS SOLUTIONS, use the following
 *   guidance to extract the information properly: The invoice date sits above
 *   the account number, rather than by the logo.
 *
 * Vendor and Guidance were two columns from owner ruling 2026-08-18 ("you said
 * two columns, but then you said 'plus' a bunch of other things … use
 * additional columns"), and the four metric columns that ruling added were
 * already dropped on 2026-09-11. What is left reads as the rule it is.
 */
function guidanceSentence(g: VendorExtractionGuidance): string {
  return `When an invoice arrives from ${guidanceVendorLabel(g)}, use the following guidance to extract the information properly: ${g.guidance_text}`
}

function GuidanceManager() {
  const toast = useToast()
  const [all, setAll] = useState<VendorExtractionGuidance[] | null>(null)
  const [status, setStatus] = useState<StatusFilter>(DEFAULT_STATUS_FILTER)
  const [error, setError] = useState<string | null>(null)
  const [edit, setEdit] = useState<VendorExtractionGuidance | null>(null)
  const [adding, setAdding] = useState(false)
  const [term, setTerm] = useState('')
  const [source, setSource] = useState<string[]>(DEFAULT_RULE_SOURCE_FILTER)

  const load = useCallback(async () => {
    setError(null)
    try {
      setAll(await loadVendorExtractionGuidance())
    } catch (e) {
      setError(errorMessage(e))
      setAll([])
    }
  }, [])
  useEffect(() => {
    void load()
  }, [load])

  const visible = useMemo(() => {
    const q = term.trim().toLowerCase()
    const haystack = (g: VendorExtractionGuidance) =>
      `${guidanceVendorHaystack(g)} ${g.guidance_text}`.toLowerCase()
    return (all ?? [])
      .filter((g) => matchesStatus(status, g.is_active))
      // Guidance the training loop wrote from corrections names the invoice
      // behind it; guidance a person wrote names none (D-367).
      .filter((g) => matchesRuleSource(source, g.source_invoice_id !== null))
      .filter((g) => !q || haystack(g).includes(q))
  }, [all, status, source, term])

  if (all === null) return <Spinner label="Loading vendor guidance…" />

  return (
    <div>
      {error && <ErrorBanner message={error} />}
      {/* The sentence that stood here is the tab's subheader since 2026-09-11
          — moved, not deleted — and its CURRENT wording came from the manual-add
          work that landed the same day, so `RULES_SECTION_SUBHEADER.guidance`
          carries that text rather than owner item 27's original. The supersede
          rule both of them replaced still shows where it bites: inside the
          editor, on the row being replaced. */}
      {/* The page's own tools live in the header's top-right cluster since
          2026-09-14 (owner) — see `components/ScreenActions.tsx`. */}
      <ScreenActions>
        <Tooltip content="Add Rule" relationship="label">
          <Button appearance="secondary" icon={<AddRegular />} onClick={() => setAdding(true)} />
        </Tooltip>
        <ListFilters
          status={status}
          onStatusChange={setStatus}
          term={term}
          onTermChange={setTerm}
          searchPlaceholder="Search vendor or guidance text…"
          ariaLabel="Vendor Guidance"
          selects={[sourceSelect(source, setSource)]}
        />
      </ScreenActions>
      <RuleTable
        rows={visible}
        emptyText={(all ?? []).length === 0 ? 'No guidance authored yet.' : 'No guidance matches.'}
        ariaLabel="Vendor extraction guidance"
        selectAllLabel="vendor extraction guidance"
        keyOf={rowId}
        isActive={rowIsActive}
        sentence={guidanceSentence}
        status={guidanceStatus}
        statusSortKey={guidanceStatusSortKey}
        write={writeGuidanceActive}
        noun="guidance"
        onEdit={setEdit}
        onMarked={(m: string) => {
          toast(m)
          void load()
        }}
        onError={setError}
      />
      <GuidanceAddDialog
        open={adding}
        /* The manager has already read every row, so the "this vendor already
           has guidance" warning is a lookup in what is on screen, not a
           second fetch. */
        rows={all ?? []}
        onClose={() => setAdding(false)}
        onSaved={(replaced) => {
          setAdding(false)
          toast(replaced ? 'The guidance was replaced.' : 'The guidance was added.')
          void load()
        }}
      />
      <GuidanceDialog
        row={edit}
        onClose={() => setEdit(null)}
        onSaved={(message) => {
          setEdit(null)
          toast(message)
          void load()
        }}
      />
    </div>
  )
}

/** Guidance's Status cell: the dot, and "Needs review" under it on a row the
    authoring loop stopped rewriting. */
function GuidanceStatus({ row }: { row: VendorExtractionGuidance }) {
  const s = useStyles()
  return (
    <div className={s.guidanceStatusCell}>
      <StatusDot active={row.is_active} />
      {row.convergence_stopped && (
        <span
          className={s.guidanceConverged}
          title="Rewritten three times without corrections falling — worth a person's look."
        >
          Needs review
        </span>
      )}
    </div>
  )
}

const guidanceStatus = (g: VendorExtractionGuidance) => <GuidanceStatus row={g} />
/** The "Needs review" line is a second fact about the same row, so it sorts
    with the status rather than on its own. */
const guidanceStatusSortKey = (g: VendorExtractionGuidance) =>
  `${g.is_active ? 'Active' : 'Inactive'} ${g.convergence_stopped ? '1' : '0'}`
const writeGuidanceActive = (g: VendorExtractionGuidance, active: boolean) =>
  updateVendorExtractionGuidanceActive(g.id, { is_active: active, row_version: g.row_version })


/**
 * Add Guidance (M6, owner 2026-09-11): a vendor and a sentence, and nothing
 * else asked. The version, the authoring loop's own `field` and `symptom`
 * bookkeeping, the null `source_invoice_id` and the convergence counters are
 * all written server-side.
 *
 * The server refuses text carrying an email address with a 400 (since the
 * owner's ruling of 2026-10-03 printed labels and example values are allowed),
 * and the refusal is written for the author — so it is shown HERE, verbatim,
 * rather than handed to the manager's banner behind the dialog, and the dialog
 * stays open with the text intact so it can be reworded.
 */
function GuidanceAddDialog({
  open,
  rows,
  onClose,
  onSaved,
}: {
  open: boolean
  /** Every row the manager has already loaded — the source of the "this vendor
      already has guidance" warning, so opening the dialog costs no fetch. */
  rows: readonly VendorExtractionGuidance[]
  onClose: () => void
  onSaved: (replaced: boolean) => void
}) {
  const s = useStyles()
  const df = useDialogFormStyles()
  const [vendor, setVendor] = useState<{ id?: string; name?: string }>({})
  const [instruction, setInstruction] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setVendor({})
    setInstruction('')
    setError(null)
  }, [open])

  // One active row per vendor (M9), so this is the row a save would replace.
  const existing = useMemo(
    () => (vendor.id ? rows.find((g) => g.is_active && g.vendor_id === vendor.id) ?? null : null),
    [rows, vendor.id],
  )

  const trimmed = instruction.trim()
  // NO LENGTH GATE HERE (owner, 2026-09-14: "remove the character limit for
  // guidance"). It used to disable Save past the cap with no counter and no
  // message — a dead button and no way to know why, which is worse than the
  // counter it replaced. The 5,000-character guard is the SERVER's, an abuse
  // bound rather than a content rule, and its refusal is a sentence this dialog
  // already shows verbatim (`error`, below).
  const ready = !!vendor.id && trimmed !== ''

  async function save() {
    if (!ready || !vendor.id) return
    setSaving(true)
    setError(null)
    try {
      await createVendorExtractionGuidance({ vendor_id: vendor.id, instruction: trimmed })
      onSaved(!!existing)
    } catch (e) {
      // A refusal is written for the author to act on — show it as the
      // server wrote it, and leave the text where it is.
      setError(
        isApiError(e) && e.code === 'validation_failed'
          ? e.message
          : writeError(e, 'Could not save guidance'),
      )
    } finally {
      setSaving(false)
    }
  }

  return (
    <AppDialog open={open} onOpenChange={(_, d) => !d.open && onClose()}>
      <DialogSurface>
        <FrozenWhileClosed open={open}><DialogBody>
          <DialogTitle>Add Rule</DialogTitle>
          <DialogContent className={mergeClasses(df.stack, df.beforeActions)}>
            {error && <ErrorBanner message={error} />}
            {/* The clauses of the rule's own sentence, in order (owner,
                2026-09-14 - see `VendorAliasDialog` for the whole rule):

                  When an invoice arrives from XEROX BUSINESS SOLUTIONS, use
                  the guidance below to extract the information properly:
              */}
            <Field label="When an invoice arrives from">
              <EntityPicker
                value={vendor}
                placeholder="e.g. XEROX BUSINESS SOLUTIONS"
                search={(t) =>
                  searchVendors(t).then<EntityOption[]>((vs) =>
                    vs.map((v) => ({
                      id: v.vendor_id,
                      primary: v.vendor_name || '(unnamed)',
                      // The invoice's Vendor picker's line (D-260): the Vendor ID,
                      // the city and state, the remittance city and state (D-241).
                      secondary: vendorOptionSubtext(v) || undefined,
                    })),
                  )
                }
                onSelect={(o) => setVendor({ id: o.id, name: o.primary })}
              />
            </Field>
            {/* THE GUIDANCE A SAVE WOULD REPLACE (docs/app.md §6 Guidance; QA
                2026-10-04 A-04): its text, read-only, and a plain sentence
                saying so. `existing` was looked up for the toast but never
                shown, so a save replaced it without a word. */}
            {existing !== null && (
              <>
                <Body1 className={s.dialogWarn}>{guidanceReplacesNotice(vendor.name ?? existing.vendor_name ?? '')}</Body1>
                <Field label="Current Guidance">
                  <FieldTextarea value={existing.instruction} readOnly appearance="filled-darker" rows={3} />
                </Field>
              </>
            )}
            <Field
              label="use the guidance below to extract the information properly:"
            >
              <FieldTextarea
                value={instruction}
                onChange={(_, d) => setInstruction(d.value)}
                rows={5}
                placeholder="e.g. The invoice date sits above the account number, not beside the logo."
              />
            </Field>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button appearance="primary" disabled={!ready || saving} onClick={() => void save()}>
              Save
            </Button>
          </DialogActions>
        </DialogBody></FrozenWhileClosed>
      </DialogSurface>
    </AppDialog>
  )
}

function GuidanceDialog({
  row,
  onClose,
  onSaved,
}: {
  row: VendorExtractionGuidance | null
  onClose: () => void
  /** The toast, from `savedToast`. */
  onSaved: (message: string) => void
}) {
  const df = useDialogFormStyles()
  // THIS DIALOG'S OWN REFUSAL (QA wave A, 2026-09-22, A-01). It used to go
  // to the manager's screen banner, which renders behind the modal.
  const [saveError, setSaveError] = useDialogError(row)
  const [instruction, setInstruction] = useState('')
  const [isActive, setIsActive] = useState(true)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!row) return
    setInstruction(row.instruction)
    setIsActive(row.is_active)
  }, [row])

  const trimmed = instruction.trim()
  const textChanged = row !== null && trimmed !== row.instruction.trim()
  const activeChanged = row !== null && isActive !== row.is_active
  // Save no longer waits for a change: an unchanged Save closes with
  // `NOTHING_CHANGED`, as every other References and Rules editor does (D-170).
  const ready = trimmed !== ''

  /**
   * TWO WRITES, in one order, because the server offers two routes and this
   * one dialog now drives both (owner item 27, 2026-08-28: the Retire button
   * became a Status field in here).
   *
   * The text goes first, as a SUPERSEDE — the server refuses to supersede a
   * retired row, so a save that both rewrote the text and set the status to
   * Inactive must write the new version while the old one is still active,
   * then retire what it just created. Doing it the other way round would 404.
   * The supersede returns the new row, so the second write has its id and its
   * fresh `row_version` without a reload in between.
   */
  async function save() {
    if (!row || !ready) return
    if (!textChanged && !activeChanged) {
      onSaved(NOTHING_CHANGED)
      return
    }
    setSaving(true)
    try {
      let target: { id: string; row_version: number } = { id: row.id, row_version: row.row_version }
      if (textChanged) {
        const created = await supersedeVendorExtractionGuidance(row.id, {
          instruction: trimmed,
          row_version: row.row_version,
        })
        target = { id: created.id, row_version: created.row_version }
      }
      if (isActive !== (textChanged ? true : row.is_active)) {
        await updateVendorExtractionGuidanceActive(target.id, {
          is_active: isActive,
          row_version: target.row_version,
        })
      }
      onSaved(savedToast('guidance', { activeBefore: row.is_active, activeAfter: isActive }))
    } catch (e) {
      setSaveError(writeError(e, 'Could not save guidance'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <AppDialog open={!!row} onOpenChange={(_, d) => !d.open && onClose()}>
      <DialogSurface>
        <FrozenWhileClosed open={!!row}><DialogBody>
          <DialogTitle>Edit Rule</DialogTitle>
          <DialogContent className={mergeClasses(df.stack, df.beforeActions)}>
            <ErrorBanner message={saveError} />
            {/* THE SAME SHAPE AS ADD RULE, PLUS STATUS (owner, 2026-09-15: "the
                Add Rule/Edit Rule pop-ups for Guidance are different. They
                should both look like Add Rule does, except that the Edit Rule
                pop-up would include the Status picker").

                They had drifted into two different dialogs about one record:
                Add read as the rule's own sentence — "When an invoice arrives
                from ... use the guidance below to extract the information
                properly:" — which is the arrangement every Add Rule pop-up on
                this screen took on 2026-09-14, and Edit still had the bare
                "Guidance" label and a different example, from before that.

                THE VENDOR IS READ-ONLY HERE, exactly as the sender editor's own
                key is: guidance is keyed on the vendor and a save is a
                SUPERSEDE, so `supersedeVendorExtractionGuidance` sends the text
                and nothing else. Pointing this rule at a different vendor would
                be a different rule, which is what Add Rule is for. */}
            <Field label="When an invoice arrives from">
              <FieldInput
                value={row ? guidanceVendorLabel(row) : ''}
                readOnly
                appearance="filled-darker"
              />
            </Field>
            {/* One free-text box, and no "what went wrong" or "field this
                addresses" dropdown above it (owner item 27): more than one
                thing can be wrong with a vendor's layout at once, and the
                confusion is often structural rather than about any one
                field. */}
            <Field
              label="use the guidance below to extract the information properly:"
            >
              <FieldTextarea
                value={instruction}
                onChange={(_, d) => setInstruction(d.value)}
                rows={5}
                placeholder="e.g. The invoice date sits above the account number, not beside the logo."
              />
            </Field>
          </DialogContent>
          <DialogActions position="start">
            <StatusToggle value={isActive} onChange={setIsActive} />
          </DialogActions>
          <DialogActions>
            <Button appearance="secondary" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button appearance="primary" disabled={!ready || saving} onClick={() => void save()}>
              Save
            </Button>
          </DialogActions>
        </DialogBody></FrozenWhileClosed>
      </DialogSurface>
    </AppDialog>
  )
}
